/**
 * Supabase DB access layer.
 * DB rows → domain: use mappers (./db/mappers). Column sets: ./db/contracts. Relations: ./db/normalizers.
 */
import { supabase } from "./supabase";
import { Deck, Card, Folder, StudyModes, DeckLang, DeckType } from "@/types/linguadeck";
import type { DecksRow, DeckSummaryRow, CardsRow } from "@/types/database";
import {
  DECK_SELECT_COLUMNS,
  DECK_SELECT_COLUMNS_SAFE,
  DECK_SUMMARY_SELECT,
  DECK_SUMMARY_SELECT_SAFE,
} from "./db/contracts";
import {
  mapDeckRowToListItem,
  mapDeckSummaryRowToDeckListItem,
  mapDeckRowsWithCards,
  mapCardRow,
  mapFolderRow,
  type DeckListItem,
} from "./db/mappers";
import { normalizeOneRelation } from "./db/normalizers";
import {
  logFlowDiagnostic,
  classifySupabaseError,
  getDebugFlags,
  validateWriteReadConsistency,
  shouldValidateReadAfterWrite,
  validateLibraryParams,
  validateNonEmptyString,
  validateId,
  inspectQueryError,
} from "@/diagnostics";
import type { UserCardProgressRow, UserCardProgress, CardInteractionInput, SmartReviewOptions, SmartReviewSessionResult } from "@/lib/adaptiveLearning/types";
import { useTimeTrackingStore } from "@/stores/useTimeTrackingStore";
import { buildSmartReviewSession, recomputeAdaptiveMetrics } from "@/lib/adaptiveLearning/sessionBuilder";
import type { SmartReviewCandidate } from "@/lib/adaptiveLearning/sessionBuilder";
import { parseStudyModes } from "./db/mappers";
import { normalizeSharedToken } from "@/lib/sharedLinks";

export { getDeckCardCount } from "./db/mappers";

const DEFAULT_STUDY_MODES: StudyModes = { cards: true, learn: true, test: true, match: true, philology: true };

/** Thrown when user tries to import a deck they already have (by source_deck_id). */
export class DeckAlreadyImportedError extends Error {
  readonly code = "DECK_ALREADY_IMPORTED" as const;
  constructor() {
    super("Deck already imported");
    this.name = "DeckAlreadyImportedError";
  }
}
const DECKS_PAGE_SIZE = 500;
const CARDS_PAGE_SIZE = 1000;
const DECK_ID_QUERY_BATCH_SIZE = 100;
const CARD_INSERT_BATCH_SIZE = 50;
const LIBRARY_PAGE_SIZE = 48;
const TEACHER_LIBRARY_PAGE_SIZE = 60;
const FOLDER_DECKS_PAGE_SIZE = 48;

function generateShareToken(): string {
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

/** Deduplicate deck list by id; keep first occurrence. Prevents duplicate render from query/view overlap. */
function dedupeDecksById<T extends { id: string }>(decks: T[]): T[] {
  const seen = new Set<string>();
  return decks.filter((d) => {
    if (seen.has(d.id)) return false;
    seen.add(d.id);
    return true;
  });
}

/** Escape search term for PostgREST .or(column.ilike."%value%") so parentheses, commas, etc. work. */
function escapeIlikePattern(value: string): string {
  const escaped = value.replace(/"/g, '""');
  return `"%${escaped}%"`;
}

function isMissingRelationOrColumnError(error: { code?: string | number; message?: string } | null | undefined): boolean {
  const code = error?.code;
  const codeStr = code != null ? String(code) : "";
  const message = String(error?.message ?? "").toLowerCase();
  return (
    codeStr === "PGRST205" ||
    codeStr === "PGRST100" ||
    codeStr === "PGRST116" ||
    codeStr === "42703" ||
    codeStr === "400" ||
    /schema cache/i.test(message) ||
    /could not find the table/i.test(message) ||
    /relation .* does not exist/i.test(message) ||
    /column .* does not exist/i.test(message) ||
    /invalid.*filter/i.test(message) ||
    /syntax.*error/i.test(message)
  );
}

async function fetchFolderShareTokenFromTable(folderId: string): Promise<string | null | undefined> {
  const { data, error } = await supabase
    .from("folder_shares")
    .select("share_token")
    .eq("folder_id", folderId)
    .maybeSingle();

  if (error) {
    if (isMissingRelationOrColumnError(error)) {
      return undefined;
    }
    throw error;
  }

  return typeof data?.share_token === "string" && data.share_token.trim() ? data.share_token : null;
}

async function fetchLegacyFolderShareToken(folderId: string): Promise<string | null | undefined> {
  const { data, error } = await supabase
    .from("folders")
    .select("share_token")
    .eq("id", folderId)
    .maybeSingle();

  if (error) {
    if (isMissingRelationOrColumnError(error)) {
      return undefined;
    }
    throw error;
  }

  return typeof data?.share_token === "string" && data.share_token.trim() ? data.share_token : null;
}

async function upsertFolderShareTokenInTable(folderId: string, shareToken: string): Promise<boolean> {
  const { error } = await supabase.from("folder_shares").upsert(
    {
      folder_id: folderId,
      share_token: shareToken,
    },
    { onConflict: "folder_id" }
  );

  if (error) {
    if (isMissingRelationOrColumnError(error)) {
      return false;
    }
    throw error;
  }

  return true;
}

async function updateLegacyFolderShareToken(folderId: string, shareToken: string): Promise<boolean> {
  const { error } = await supabase
    .from("folders")
    .update({ share_token: shareToken })
    .eq("id", folderId);

  if (error) {
    if (isMissingRelationOrColumnError(error)) {
      return false;
    }
    throw error;
  }

  return true;
}

async function ensureDeckShareTokens(deckIds: string[]): Promise<void> {
  if (deckIds.length === 0) return;

  const { data: deckRows, error: decksError } = await supabase
    .from("decks")
    .select("id, share_token, share_access")
    .in("id", deckIds);

  if (decksError) throw decksError;

  const missingTokens = (deckRows ?? []).filter(
    (deck) => typeof deck.share_token !== "string" || deck.share_token.trim() === ""
  );

  for (const deck of missingTokens) {
    const { error } = await supabase
      .from("decks")
      .update({
        share_token: generateShareToken(),
        share_access: deck.share_access ?? "read",
      })
      .eq("id", deck.id);
    if (error) throw error;
  }
}

async function fetchSharedFolderFromShareTable(
  token: string
): Promise<{ folder: Folder; decks: Deck[] } | null | undefined> {
  const { data, error } = await supabase
    .from("folder_shares")
    .select("share_token, folder:folders(*)")
    .eq("share_token", token)
    .maybeSingle();

  if (error) {
    if (isMissingRelationOrColumnError(error)) {
      return undefined;
    }
    throw error;
  }

  type FolderRelation = { id: string; user_id: string; title: string; created_at: string; share_token?: string | null };
  const folderRow = normalizeOneRelation<FolderRelation>(data?.folder);
  if (
    !folderRow ||
    typeof folderRow.id !== "string" ||
    typeof folderRow.user_id !== "string" ||
    typeof folderRow.title !== "string" ||
    typeof folderRow.created_at !== "string"
  )
    return null;

  const deckIds = await fetchFolderDeckIds(folderRow.id);
  let decks: Deck[];
  if (deckIds.length > 0) {
    const useSummary = await deckSummariesAvailable();
    if (useSummary) {
      const runSummaryQuery = async (selectCols: string) => {
        const allRows: DeckSummaryRow[] = [];
        for (let i = 0; i < deckIds.length; i += DECK_ID_QUERY_BATCH_SIZE) {
          const batch = deckIds.slice(i, i + DECK_ID_QUERY_BATCH_SIZE);
          const { data: rows, error } = await supabase
            .from("deck_summaries")
            .select(selectCols)
            .in("deck_id", batch);
          if (error) throw error;
          if (rows?.length) allRows.push(...(rows as DeckSummaryRow[]));
        }
        if (allRows.length === 0) return null;
        const orderMap = new Map(deckIds.map((id, i) => [id, i]));
        const items = allRows.map(mapDeckSummaryRowToDeckListItem);
        items.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
        return items as Deck[];
      };
      try {
        let result: Deck[] | null = null;
        try {
          result = await runSummaryQuery(DECK_SUMMARY_SELECT);
        } catch (e) {
          if (isMissingRelationOrColumnError(e as { code?: string; message?: string })) {
            result = await runSummaryQuery(DECK_SUMMARY_SELECT_SAFE);
          } else {
            throw e;
          }
        }
        if (result) {
          const foundIds = new Set(result.map((d) => d.id));
          const missingIds = deckIds.filter((id) => !foundIds.has(id));
          if (missingIds.length > 0) {
            const extra = await fetchDecksByIds(missingIds);
            const orderMap = new Map(deckIds.map((id, i) => [id, i]));
            decks = [...result, ...extra].sort(
              (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
            );
          } else {
            decks = result;
          }
        } else {
          decks = await fetchDecksByIds(deckIds);
        }
      } catch {
        decks = await fetchDecksByIds(deckIds);
      }
    } else {
      decks = await fetchDecksByIds(deckIds);
    }
  } else {
    decks = [];
  }

  return {
    folder: mapFolderRow({
      ...folderRow,
      share_token: typeof data?.share_token === "string" ? data.share_token : null,
    }),
    decks,
  };
}

async function fetchAllDecksRowsForUser(userId: string, useSafeColumns = false): Promise<DecksRow[]> {
  const selectCols = useSafeColumns ? DECK_SELECT_COLUMNS_SAFE : DECK_SELECT_COLUMNS;
  const rows: DecksRow[] = [];

  for (let from = 0; ; from += DECKS_PAGE_SIZE) {
    const { data, error } = await supabase
      .from("decks")
      .select(selectCols)
      .eq("user_id", userId)
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + DECKS_PAGE_SIZE - 1);

    if (error) {
      if (!useSafeColumns && isMissingRelationOrColumnError(error)) {
        return fetchAllDecksRowsForUser(userId, true);
      }
      throw error;
    }

    const batch = (data ?? []) as unknown as DecksRow[];
    rows.push(...batch);

    if (batch.length < DECKS_PAGE_SIZE) {
      break;
    }
  }

  return rows;
}

async function fetchAllCardsForDeckIds(deckIds: string[]): Promise<CardsRow[]> {
  if (deckIds.length === 0) return [];

  const rows: CardsRow[] = [];

  for (let i = 0; i < deckIds.length; i += DECK_ID_QUERY_BATCH_SIZE) {
    const deckIdBatch = deckIds.slice(i, i + DECK_ID_QUERY_BATCH_SIZE);

    for (let from = 0; ; from += CARDS_PAGE_SIZE) {
      const { data, error } = await supabase
        .from("cards")
        .select("*")
        .in("deck_id", deckIdBatch)
        .order("deck_id", { ascending: true })
        .order("position", { ascending: true })
        .order("id", { ascending: true })
        .range(from, from + CARDS_PAGE_SIZE - 1);

      if (error) throw error;

      const batch = (data ?? []) as CardsRow[];
      rows.push(...batch);

      if (batch.length < CARDS_PAGE_SIZE) {
        break;
      }
    }
  }

  return rows;
}

async function insertCardsRowsInBatches(cardRows: Record<string, unknown>[]): Promise<void> {
  if (cardRows.length === 0) return;

  for (let i = 0; i < cardRows.length; i += CARD_INSERT_BATCH_SIZE) {
    const batch = cardRows.slice(i, i + CARD_INSERT_BATCH_SIZE);
    const { error } = await supabase.from("cards").insert(batch);
    if (error) throw error;
  }
}

// ─── Decks ────────────────────────────────────────────────────────────────────

export async function fetchDecks(userId: string): Promise<Deck[]> {
  const deckRows = await fetchAllDecksRowsForUser(userId);
  if (deckRows.length === 0) return [];

  const cardRows = await fetchAllCardsForDeckIds(deckRows.map((deck) => deck.id));
  return mapDeckRowsWithCards(deckRows, cardRows);
}

function fetchDecksLightLegacy(userId: string): Promise<DeckListItem[]> {
  return (async () => {
    const deckRows = await fetchAllDecksRowsForUser(userId);
    if (deckRows.length === 0) return [];
    const cardCounts = await fetchCardCountsByDeckIds(deckRows.map((d) => d.id));
    return deckRows.map((deck) =>
      mapDeckRowToListItem(deck, cardCounts.get(deck.id) ?? 0)
    );
  })();
}

/** Lightweight: from deck_summaries (single source of truth). Falls back to decks when view/query fails.
 * @param initialLimit - If set, fetch only this many for fast first paint; omit for full fetch. */
export async function fetchDecksLight(userId: string, initialLimit?: number): Promise<DeckListItem[]> {
  const r = validateNonEmptyString(userId, "userId");
  if (!r.valid) {
    logFlowDiagnostic({
      flow: "library.fetchDecksLight",
      operation: "select",
      entity: "deck",
      success: false,
      error: new Error(r.error),
    });
    console.warn("[fetchDecksLight] Empty userId, returning []");
    return [];
  }
  const start = typeof performance !== "undefined" ? performance.now() : 0;
  const pageSize = initialLimit != null ? Math.min(initialLimit, DECKS_PAGE_SIZE) : DECKS_PAGE_SIZE;

  const runSummaryQuery = async (selectCols: string): Promise<DeckListItem[]> => {
    const items: DeckListItem[] = [];
    for (let from = 0; ; from += pageSize) {
      const limit = initialLimit != null ? Math.min(pageSize, initialLimit - items.length) : DECKS_PAGE_SIZE;
      if (limit <= 0) break;
      const { data, error } = await supabase
        .from("deck_summaries")
        .select(selectCols)
        .eq("owner_id", userId)
        .order("created_at", { ascending: false })
        .order("deck_id", { ascending: true })
        .range(from, from + limit - 1);
      if (error) throw error;
      const rows = (data ?? []) as unknown as DeckSummaryRow[];
      items.push(...rows.map(mapDeckSummaryRowToDeckListItem));
      if (rows.length < limit || (initialLimit != null && items.length >= initialLimit)) break;
    }
    return items;
  };

  try {
    let items: DeckListItem[];
    try {
      items = await runSummaryQuery(DECK_SUMMARY_SELECT);
    } catch (summaryError) {
      if (isMissingRelationOrColumnError(summaryError as { code?: string; message?: string })) {
        try {
          items = await runSummaryQuery(DECK_SUMMARY_SELECT_SAFE);
        } catch {
          throw summaryError;
        }
      } else {
        throw summaryError;
      }
    }
    // CRITICAL: When deck_summaries returns 0 rows but user owns decks, fall back to decks table
    // (canonical ownership source) so My Library never hides owned decks (RLS/view edge cases).
    if (items.length === 0) {
      const legacy = await fetchDecksLightLegacy(userId);
      if (legacy.length > 0) {
        if (getDebugFlags().libraryDiagnostics) {
          console.debug("[StudyDo library] fetchDecksLight: deck_summaries empty but legacy has decks, using legacy", { legacyCount: legacy.length });
        }
        return dedupeDecksById(legacy);
      }
    }
    const durationMs = typeof performance !== "undefined" ? Math.round(performance.now() - start) : 0;
    logFlowDiagnostic({
      flow: "library.fetchDecksLight",
      operation: "select",
      entity: "deck",
      userId,
      success: true,
      durationMs,
    });
    if (getDebugFlags().libraryDiagnostics) {
      console.debug("[StudyDo library] fetchDecksLight: deck_summaries ok", { count: items.length, durationMs });
    }
    return dedupeDecksById(items);
  } catch (e) {
    const classified = classifySupabaseError(e);
    logFlowDiagnostic({
      flow: "library.fetchDecksLight",
      operation: "select",
      entity: "deck",
      userId,
      success: false,
      error: e,
      classified,
    });
    console.warn("[fetchDecksLight] Falling back to legacy:", e);
    const legacy = await fetchDecksLightLegacy(userId);
    if (getDebugFlags().libraryDiagnostics) {
      console.debug("[StudyDo library] fetchDecksLight: exception, legacy used", { count: legacy.length });
    }
    return dedupeDecksById(legacy);
  }
}

// ─── Library pagination (My Library, Teacher Library, Folders) ─────────────────

export type LibrarySort = "recent" | "alphabetical" | "language" | "tag" | "type";

export interface LibraryPageParams {
  userId: string;
  limit: number;
  offset: number;
  /** Tab filter: cards, learn, test, games — filters by study_modes */
  tab?: "cards" | "learn" | "test" | "games" | "philology";
  search?: string;
  sort?: LibrarySort;
  language?: string | null;
  tag?: string | null;
  typeFilter?: "cards" | "learn" | "test" | "philology" | null;
}

/** Deck list item with card count; cards array is empty for list performance. */
export type { DeckListItem } from "./db/mappers";

export interface LibraryPageResult {
  items: DeckListItem[];
  total: number;
}

/** Check if deck_summaries view exists. Used only for optional fallback paths; primary fetchers always try deck_summaries first. */
async function deckSummariesAvailable(): Promise<boolean> {
  const { error } = await supabase.from("deck_summaries").select("deck_id").limit(1);
  if (error && isMissingRelationOrColumnError(error)) return false;
  return !error;
}

/** Fetch paginated deck list for My Library from deck_summaries. Fallback to decks only on relation-not-found. */
export async function fetchLibraryPage(params: LibraryPageParams): Promise<LibraryPageResult> {
  const {
    userId,
    limit,
    offset,
    tab,
    search,
    sort = "recent",
    language,
    tag,
    typeFilter,
  } = params;

  const validation = validateLibraryParams({ userId, limit, offset, sort });
  if (!validation.valid) {
    const err = new Error(validation.error);
    logFlowDiagnostic({
      flow: "library.fetchLibraryPage",
      operation: "select",
      entity: "deck",
      success: false,
      error: err,
    });
    throw err;
  }

  const modeKey = tab ?? typeFilter;

  try {
    let query = supabase
      .from("deck_summaries")
      .select(DECK_SUMMARY_SELECT, { count: "exact" })
      .eq("owner_id", userId);

    if (search?.trim()) {
      const q = search.trim();
      const pattern = escapeIlikePattern(q);
      query = query.or(`title.ilike.${pattern},source_language.ilike.${pattern},level.ilike.${pattern},deck_type.ilike.${pattern}`);
    }
    if (language) query = query.eq("source_language", language);
    if (tag?.trim()) {
      query = query.or(`deck_type.eq.${tag.trim()},level.eq.${tag.trim()}`);
    }
    if (modeKey) {
      query = query.contains("study_modes", { [modeKey]: true });
      if (modeKey === "cards") query = query.gt("cards_count", 0);
      else if (modeKey === "learn") query = query.gt("learnable_count", 0);
      else if (modeKey === "test") query = query.gt("testable_count", 0);
      else if (modeKey === "games") query = query.gt("games_count", 0);
    }

    switch (sort) {
      case "alphabetical":
        query = query.order("title", { ascending: true }).order("deck_id", { ascending: true });
        break;
      case "language":
        query = query.order("source_language", { ascending: true }).order("title", { ascending: true }).order("deck_id", { ascending: true });
        break;
      case "tag":
        query = query.order("deck_type", { ascending: true }).order("level", { ascending: true }).order("title", { ascending: true }).order("deck_id", { ascending: true });
        break;
      case "type":
        query = query.order("type", { ascending: true }).order("created_at", { ascending: false }).order("deck_id", { ascending: true });
        break;
      default:
        query = query.order("created_at", { ascending: false }).order("deck_id", { ascending: true });
    }

    const { data: rows, error, count } = await query.range(offset, offset + limit - 1);
    if (error) {
      if (isMissingRelationOrColumnError(error)) throw error;
      throw error;
    }
    const summaryRows = (rows ?? []) as DeckSummaryRow[];
    const items = summaryRows.map(mapDeckSummaryRowToDeckListItem);
    return { items, total: count ?? 0 };
  } catch (e) {
    if (!isMissingRelationOrColumnError(e as { code?: string; message?: string })) {
      console.warn("[fetchLibraryPage] deck_summaries error, falling back to decks:", e);
    }
  }

  let query = supabase
    .from("decks")
    .select(DECK_SELECT_COLUMNS, { count: "exact" })
    .eq("user_id", userId);

  if (search?.trim()) {
    const q = search.trim();
    const pattern = escapeIlikePattern(q);
    query = query.or(`title.ilike.${pattern},lang.ilike.${pattern},level.ilike.${pattern},deck_type.ilike.${pattern}`);
  }
  if (language) query = query.eq("lang", language);
  if (tag?.trim()) {
    query = query.or(`deck_type.eq.${tag.trim()},level.eq.${tag.trim()}`);
  }
  if (modeKey) {
    query = query.contains("study_modes", { [modeKey]: true });
  }

  switch (sort) {
    case "alphabetical":
      query = query.order("title", { ascending: true }).order("id", { ascending: true });
      break;
    case "language":
      query = query.order("lang", { ascending: true }).order("title", { ascending: true }).order("id", { ascending: true });
      break;
    case "tag":
      query = query.order("deck_type", { ascending: true }).order("level", { ascending: true }).order("title", { ascending: true }).order("id", { ascending: true });
      break;
    case "type":
      query = query.order("type", { ascending: true }).order("created_at", { ascending: false }).order("id", { ascending: true });
      break;
    default:
      query = query.order("created_at", { ascending: false }).order("id", { ascending: true });
  }

  const { data: rows, error, count } = await query.range(offset, offset + limit - 1);
  if (error) throw error;
  const deckRows = (rows ?? []) as DecksRow[];
  if (deckRows.length === 0) return { items: [], total: count ?? 0 };
  const deckIds = deckRows.map((d) => d.id);
  const cardCounts = await fetchCardCountsByDeckIds(deckIds);
  const items = deckRows.map((deck) =>
    mapDeckRowToListItem(deck, cardCounts.get(deck.id) ?? 0)
  );
  return { items, total: count ?? 0 };
}

/** Fetches card counts for given deck IDs. Used to fix decks with stale/0 counts from deck_summaries. */
export async function fetchCardCountsByDeckIds(deckIds: string[]): Promise<Map<string, number>> {
  if (deckIds.length === 0) return new Map();
  const map = new Map<string, number>();
  for (let i = 0; i < deckIds.length; i += DECK_ID_QUERY_BATCH_SIZE) {
    const batch = deckIds.slice(i, i + DECK_ID_QUERY_BATCH_SIZE);
    const { data, error } = await supabase
      .from("cards")
      .select("deck_id")
      .in("deck_id", batch);
    if (error) {
      if (getDebugFlags().libraryDiagnostics) {
        console.warn("[fetchCardCountsByDeckIds] cards query failed, using 0 counts:", error?.code, error?.message);
      }
      continue;
    }
    for (const row of data ?? []) {
      const id = (row as { deck_id: string }).deck_id;
      map.set(id, (map.get(id) ?? 0) + 1);
    }
  }
  return map;
}

export interface TeacherLibraryPageParams {
  teacherId: string;
  limit: number;
  offset: number;
  typeFilter?: "cards" | "learn" | "test" | "match" | "philology" | null;
}

export interface TeacherLibraryPageResult {
  items: Deck[];
  total: number;
}

/** Fetch paginated teacher library from deck_summaries (or decks). No card bodies until study. */
export async function fetchTeacherLibraryPage(params: TeacherLibraryPageParams): Promise<TeacherLibraryPageResult> {
  const { teacherId, limit, offset, typeFilter } = params;

  const useSummary = await deckSummariesAvailable();
  if (useSummary) {
    try {
      let query = supabase
        .from("deck_summaries")
        .select(DECK_SUMMARY_SELECT, { count: "exact" })
        .eq("owner_id", teacherId)
        .order("created_at", { ascending: false })
        .order("deck_id", { ascending: true });

      if (typeFilter) {
        query = query.contains("study_modes", { [typeFilter]: true });
        if (typeFilter === "cards") query = query.gt("cards_count", 0);
        else if (typeFilter === "learn") query = query.gt("learnable_count", 0);
        else if (typeFilter === "test") query = query.gt("testable_count", 0);
        else if (typeFilter === "match") query = query.gt("games_count", 0);
      }

      const { data: rows, error, count } = await query.range(offset, offset + limit - 1);
      if (error) throw error;
      const summaryRows = (rows ?? []) as DeckSummaryRow[];
      const items = summaryRows.map(mapDeckSummaryRowToDeckListItem) as Deck[];
      return { items, total: count ?? 0 };
    } catch {
      /* fall through to legacy path */
    }
  }

  let query = supabase
    .from("decks")
    .select(DECK_SELECT_COLUMNS, { count: "exact" })
    .eq("user_id", teacherId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: true });

  if (typeFilter) {
    query = query.contains("study_modes", { [typeFilter]: true });
  }

  const { data: rows, error, count } = await query.range(offset, offset + limit - 1);
  if (error) throw error;
  const deckRows = (rows ?? []) as DecksRow[];
  if (deckRows.length === 0) return { items: [], total: count ?? 0 };
  const cardRows = await fetchAllCardsForDeckIds(deckRows.map((d) => d.id));
  const items = mapDeckRowsWithCards(deckRows, cardRows);
  return { items, total: count ?? 0 };
}

// ─── Global Library (public shared decks catalog) ─────────────────────────────

export interface GlobalLibraryPageParams {
  limit: number;
  offset: number;
  search?: string;
  language?: string | null;
  sort?: "recent" | "alphabetical" | "language";
  typeFilter?: "cards" | "learn" | "test" | "match" | "philology" | null;
  /** Only decks created today (UTC). */
  dateFilter?: "all" | "today" | null;
}

export interface GlobalLibraryPageResult {
  items: Deck[];
  total: number;
}

function getTodayUtcBounds(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const to = new Date(from.getTime() + 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Fetch paginated global decks: ALL decks from all users (catalog). Requires RLS policy "Authenticated can read all decks for Global Library". */
export async function fetchGlobalLibraryPage(params: GlobalLibraryPageParams): Promise<GlobalLibraryPageResult> {
  const { limit, offset, search, language, sort = "recent", typeFilter, dateFilter } = params;
  const todayBounds = dateFilter === "today" ? getTodayUtcBounds() : null;

  try {
      // Use SAFE columns so deck_summaries works even when view lacks deck_type/description/type/study_modes (older Supabase).
      // Only show originals in catalog: exclude user copies (decks with source_deck_id set).
      let query = supabase
        .from("deck_summaries")
        .select(DECK_SUMMARY_SELECT_SAFE, { count: "exact" })
        .is("source_deck_id", null);

      if (search?.trim()) {
        const q = search.trim();
        const pattern = escapeIlikePattern(q);
        query = query.or(`title.ilike.${pattern},source_language.ilike.${pattern},level.ilike.${pattern}`);
      }
      if (language) query = query.eq("source_language", language);
      if (todayBounds) {
        query = query.gte("created_at", todayBounds.from).lt("created_at", todayBounds.to);
      }
      // Global Library: typeFilter is launch-context only (CTA/mode). Do NOT filter catalog by
      // learnable_count, testable_count, games_count, or cards_count — show the same public deck
      // universe across Cards, Learn, Test, Games. Tab controls CTA label and launch route only.

      switch (sort) {
        case "alphabetical":
          query = query.order("title", { ascending: true }).order("deck_id", { ascending: true });
          break;
        case "language":
          query = query.order("source_language", { ascending: true }).order("title", { ascending: true }).order("deck_id", { ascending: true });
          break;
        default:
          query = query.order("created_at", { ascending: false }).order("deck_id", { ascending: true });
      }

      const { data: rows, error, count } = await query.range(offset, offset + limit - 1);
      if (error) {
        console.log("[fetchGlobalLibraryPage] deck_summaries error", { code: error.code, message: error.message });
        if (isMissingRelationOrColumnError(error)) {
          /* fall through to legacy */
        } else {
          console.warn("[fetchGlobalLibraryPage] deck_summaries error:", error);
          throw error;
        }
      } else {
        const summaryRows = (rows ?? []) as DeckSummaryRow[];
        const items = dedupeDecksById(summaryRows.map(mapDeckSummaryRowToDeckListItem) as Deck[]);
        return { items, total: count ?? 0 };
      }
  } catch (e) {
    if (!isMissingRelationOrColumnError(e as { code?: string; message?: string })) {
      console.warn("[fetchGlobalLibraryPage] Error, falling back to legacy:", e);
    }
  }

  // Only show originals in catalog: exclude user copies (decks with source_deck_id set).
  let query = supabase
    .from("decks")
    .select(DECK_SELECT_COLUMNS, { count: "exact" })
    .is("source_deck_id", null);

  if (search?.trim()) {
    const q = search.trim();
    const pattern = escapeIlikePattern(q);
    query = query.or(`title.ilike.${pattern},lang.ilike.${pattern},level.ilike.${pattern},deck_type.ilike.${pattern}`);
  }
  if (language) query = query.eq("lang", language);
  if (todayBounds) {
    query = query.gte("created_at", todayBounds.from).lt("created_at", todayBounds.to);
  }
  // Global Library: typeFilter is launch-context only; do not filter catalog in legacy path
  // (deck_summaries path applies cards_count > 0 for "cards" tab only; legacy has no counts)

  switch (sort) {
    case "alphabetical":
      query = query.order("title", { ascending: true }).order("id", { ascending: true });
      break;
    case "language":
      query = query.order("lang", { ascending: true }).order("title", { ascending: true }).order("id", { ascending: true });
      break;
    default:
      query = query.order("created_at", { ascending: false }).order("id", { ascending: true });
  }

  const { data: rows, error, count } = await query.range(offset, offset + limit - 1);
  if (error) {
    console.log("[fetchGlobalLibraryPage] LEGACY decks error", { code: error.code, message: error.message });
    throw error;
  }
  const deckRows = (rows ?? []) as DecksRow[];
  if (deckRows.length === 0) {
    console.log("[fetchGlobalLibraryPage] LEGACY returned 0 deck rows");
    return { items: [], total: count ?? 0 };
  }
  const cardRows = await fetchAllCardsForDeckIds(deckRows.map((d) => d.id));
  const items = dedupeDecksById(mapDeckRowsWithCards(deckRows, cardRows));
  console.log("[fetchGlobalLibraryPage] LEGACY", { itemsCount: items.length, total: count ?? 0 });
  return { items, total: count ?? 0 };
}

/** Fetch a single deck by ID for study or add-to-library (read-only). All decks visible in Global Library. */
export async function fetchGlobalDeckForStudy(deckId: string): Promise<Deck | null> {
  const { data: deckData, error } = await supabase
    .from("decks")
    .select("*")
    .eq("id", deckId)
    .maybeSingle();

  if (error || !deckData) return null;

  const cardRows = await fetchAllCardsForDeckIds([deckData.id]);
  return {
    id: deckData.id,
    title: deckData.title,
    lang: deckData.lang,
    emoji: deckData.emoji ?? "📚",
    level: deckData.level ?? undefined,
    type: ((deckData as { type?: string }).type as "words" | "cards" | "tests") ?? "cards",
    deckType: (deckData as { deck_type?: string }).deck_type ?? undefined,
    shareToken: (deckData as { share_token?: string }).share_token ?? null,
    shareAccess: ((deckData as { share_access?: string }).share_access as "read" | "write") ?? "read",
    studyModes: parseStudyModes((deckData as { study_modes?: StudyModes | string | null }).study_modes),
    created_at: (deckData as { created_at?: string }).created_at ?? undefined,
    cards: cardRows.map((c) => mapCardRow(c)),
  };
}

export interface FolderDecksPageParams {
  folderId: string;
  limit: number;
  offset: number;
}

export interface FolderDecksPageResult {
  items: Deck[];
  total: number;
}

/** Fetch paginated decks for a folder from deck_summaries (or full decks). No card bodies when using summary. */
export async function fetchFolderDecksPage(params: FolderDecksPageParams): Promise<FolderDecksPageResult> {
  const { folderId, limit, offset } = params;
  const deckIds = await fetchFolderDeckIds(folderId);
  if (deckIds.length === 0) return { items: [], total: 0 };

  const total = deckIds.length;
  const pageIds = deckIds.slice(offset, offset + limit);
  if (pageIds.length === 0) return { items: [], total };

  const useSummary = await deckSummariesAvailable();
  if (useSummary) {
    const runSummaryQuery = async (selectCols: string) => {
      const { data: rows, error } = await supabase
        .from("deck_summaries")
        .select(selectCols)
        .in("deck_id", pageIds);
      if (!error && rows?.length) {
        const orderMap = new Map(pageIds.map((id, i) => [id, i]));
        return (rows as DeckSummaryRow[]).map(mapDeckSummaryRowToDeckListItem).sort(
          (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
        ) as Deck[];
      }
      if (error) throw error;
      return null;
    };
    try {
      let items: Deck[] | null = null;
      try {
        items = await runSummaryQuery(DECK_SUMMARY_SELECT);
      } catch (e) {
        if (isMissingRelationOrColumnError(e as { code?: string; message?: string })) {
          items = await runSummaryQuery(DECK_SUMMARY_SELECT_SAFE);
        } else {
          throw e;
        }
      }
      if (items) {
        const foundIds = new Set(items.map((d) => d.id));
        const missingIds = pageIds.filter((id) => !foundIds.has(id));
        if (missingIds.length > 0) {
          const extra = await fetchDecksByIds(missingIds);
          const orderMap = new Map(pageIds.map((id, i) => [id, i]));
          items = [...items, ...extra].sort(
            (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
          );
        }
        return { items, total };
      }
    } catch {
      /* fall through to legacy path */
    }
  }

  const decks = await fetchDecksByIds(pageIds);
  const orderMap = new Map(pageIds.map((id, i) => [id, i]));
  decks.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
  return { items: decks, total };
}

/** Fetch deck list for Add Materials modal: paginated, newest-first, optional search. No card bodies. */
export interface AddMaterialsPageParams {
  userId: string;
  folderId: string;
  existingDeckIds: string[];
  limit: number;
  offset: number;
  search?: string;
}

export async function fetchAddMaterialsPage(params: AddMaterialsPageParams): Promise<LibraryPageResult> {
  const { userId, folderId, existingDeckIds, limit, offset, search } = params;
  let query = supabase
    .from("decks")
    .select(DECK_SELECT_COLUMNS, { count: "exact" })
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: true });

  if (search?.trim()) {
    const q = search.trim();
    const pattern = escapeIlikePattern(q);
    query = query.or(`title.ilike.${pattern},lang.ilike.${pattern}`);
  }

  const { data: rows, error, count } = await query.range(offset, offset + limit - 1);
  if (error) throw error;
  const deckRows = (rows ?? []) as DecksRow[];
  const filtered = existingDeckIds.length
    ? deckRows.filter((d) => !existingDeckIds.includes(d.id))
    : deckRows;
  const deckIds = filtered.map((d) => d.id);
  const cardCounts = await fetchCardCountsByDeckIds(deckIds);
  const items: DeckListItem[] = filtered.map((deck) =>
    mapDeckRowToListItem(deck, cardCounts.get(deck.id) ?? 0)
  );
  return { items, total: count ?? 0 };
}

/** Check if user already has a deck imported from the given source deck (by source_deck_id). */
export async function userAlreadyHasDeckFromSource(userId: string, sourceDeckId: string): Promise<boolean> {
  if (!sourceDeckId?.trim()) return false;
  const { data, error } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId)
    .eq("source_deck_id", sourceDeckId)
    .limit(1)
    .maybeSingle();
  if (error) return false;
  return Boolean(data?.id);
}

/** Fetch student's deck that was imported from the given source deck. Returns null if not found. */
export async function fetchStudentDeckBySourceId(
  userId: string,
  sourceDeckId: string
): Promise<Deck | null> {
  if (!sourceDeckId?.trim()) return null;
  const { data, error } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId)
    .eq("source_deck_id", sourceDeckId)
    .limit(1)
    .maybeSingle();
  if (error || !data?.id) return null;
  const decks = await fetchDecksByIds([data.id]);
  return decks[0] ?? null;
}

const SOURCE_DECK_ID_BATCH = 100;

/** Return map source_deck_id -> user's deck id for decks the user already imported from those sources. */
export async function fetchUserDeckIdsBySourceDeckIds(
  userId: string,
  sourceDeckIds: string[]
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const unique = [...new Set(sourceDeckIds.filter((id) => id?.trim()))];
  for (let i = 0; i < unique.length; i += SOURCE_DECK_ID_BATCH) {
    const batch = unique.slice(i, i + SOURCE_DECK_ID_BATCH);
    const { data, error } = await supabase
      .from("decks")
      .select("id, source_deck_id")
      .eq("user_id", userId)
      .in("source_deck_id", batch);
    if (error) continue;
    for (const row of data ?? []) {
      const sid = (row as { source_deck_id?: string }).source_deck_id;
      const id = (row as { id?: string }).id;
      if (sid && id) map.set(sid, id);
    }
  }
  return map;
}

export type CreateDeckOptions = {
  /** If set, prevents duplicate imports: checks that user doesn't already have a deck from this source. */
  sourceDeckId?: string;
};

export async function createDeck(
  userId: string,
  deck: Omit<Deck, "id"> | Deck,
  options?: CreateDeckOptions
): Promise<Deck> {
  const uidCheck = validateNonEmptyString(userId, "userId");
  if (!uidCheck.valid) throw new Error(`createDeck: ${uidCheck.error}`);
  const titleCheck = validateNonEmptyString(deck?.title, "deck.title");
  if (!titleCheck.valid) throw new Error(`createDeck: ${titleCheck.error}`);

  const sourceDeckId = options?.sourceDeckId?.trim();
  if (sourceDeckId) {
    const already = await userAlreadyHasDeckFromSource(userId, sourceDeckId);
    if (already) throw new DeckAlreadyImportedError();
  }

  const deckId = (deck as { id?: string }).id ?? "deck_" + Date.now();

  // When creating from "Мои слова", deck.type is 'words' — MUST include in insert for Sidebar filtering
  const type = ((deck as { type?: string }).type || "cards") as "words" | "cards" | "tests";
  const studyModes = (deck as { studyModes?: StudyModes }).studyModes ?? DEFAULT_STUDY_MODES;
  // Fallback for deck.lang when global language selector was removed; DB requires non-null lang
  const lang = deck.lang ?? "en";
  const insertPayload: Record<string, unknown> = {
    id: deckId,
    user_id: userId,
    title: deck.title.trim(),
    lang,
    emoji: deck.emoji ?? "📚",
    level: deck.level ?? null,
    deck_type: deck.deckType ?? null,
    type,
    study_modes: studyModes,
  };
  if (sourceDeckId) {
    insertPayload.source_deck_id = sourceDeckId;
  }
  const { error: deckError } = await supabase.from("decks").insert(insertPayload);

  if (deckError) {
    const classified = classifySupabaseError(deckError);
    logFlowDiagnostic({
      flow: "library.createDeck",
      operation: "insert",
      entity: "deck",
      userId,
      entityId: deckId,
      success: false,
      error: deckError,
      classified,
    });
    throw deckError;
  }

  logFlowDiagnostic({
    flow: "library.createDeck",
    operation: "insert",
    entity: "deck",
    userId,
    entityId: deckId,
    success: true,
  });

  const createdDeck: Deck = {
    ...deck,
    id: deckId,
    type,
    created_at: new Date().toISOString(),
    studyModes,
  };

  const cards = deck.cards ?? [];
  if (cards.length > 0) {
    const ts = Date.now();
    const cardRows = cards.map((card, i) => ({
      id: `card_${deckId}_${i}_${ts}`,
      deck_id: deckId,
      front: card.front,
      back: card.back,
      front_language: card.frontLang?.trim() || null,
      back_language: card.backLang?.trim() || null,
      hint: card.hint ?? null,
      definition: card.definition ?? null,
      example: card.example ?? null,
      image_url: card.imageUrl ?? null,
      card_type: card.cardType ?? null,
      level: card.level ?? null,
      position: i,
      test_data: card.testData ?? null,
    }));

    await insertCardsRowsInBatches(cardRows);

    createdDeck.cards = cards.map((card, i) => ({
      ...card,
      id: cardRows[i].id,
    }));
  }

  // Optional: verify deck appears in My Library fetch path (read-model consistency)
  if (shouldValidateReadAfterWrite("library.createDeck")) {
    validateWriteReadConsistency({
      flow: "library.createDeck",
      entity: "deck",
      entityId: deckId,
      userId,
      writeSucceeded: true,
      expectedPresence: true,
      fetchValidator: () => fetchDecksLight(userId),
      checkPresence: (list) => Array.isArray(list) && list.some((d) => d.id === deckId),
    }).catch(() => { /* non-fatal; already logged */ });
  }

  return createdDeck;
}

export type BulkCreateResult = {
  created: Deck[];
  failed: Array<{ index: number; title: string; error: Error }>;
};

/**
 * Validates a reviewed module draft before persistence.
 * Returns null if invalid, otherwise returns sanitized deck payload.
 */
function validateReviewedModule(
  draft: Omit<Deck, "id">,
  index: number
): { valid: true; deck: Omit<Deck, "id"> } | { valid: false; reason: string } {
  const title = (draft.title ?? "").trim();
  if (!title) {
    return { valid: false, reason: `Module ${index + 1}: title is required` };
  }
  const cards = draft.cards ?? [];
  const validCards = cards.filter((c) => (c?.front ?? "").trim() && (c?.back ?? "").trim());
  if (validCards.length === 0) {
    return { valid: false, reason: `Module "${title}": at least one valid card (front + back) required` };
  }
  return {
    valid: true,
    deck: {
      ...draft,
      title,
      cards: validCards,
      lang: (draft.lang ?? "en") as DeckLang,
      emoji: draft.emoji ?? "📚",
      type: (draft.type ?? "words") as DeckType,
      deckType: draft.deckType ?? "vocab",
      studyModes: draft.studyModes ?? DEFAULT_STUDY_MODES,
    },
  };
}

/**
 * Creates multiple decks from reviewed AI module drafts.
 * Each module becomes a separate deck with its own cards.
 * Uses per-module creation: successes are persisted, failures are collected and reported.
 * Does NOT use DB transactions — partial success is possible; failed modules are reported.
 */
export async function createDecksBulk(
  userId: string,
  drafts: Omit<Deck, "id">[]
): Promise<BulkCreateResult> {
  const uidCheck = validateNonEmptyString(userId, "userId");
  if (!uidCheck.valid) throw new Error(`createDecksBulk: ${uidCheck.error}`);

  const created: Deck[] = [];
  const failed: BulkCreateResult["failed"] = [];
  const baseId = Date.now();

  for (let i = 0; i < drafts.length; i++) {
    const validation = validateReviewedModule(drafts[i], i);
    if (validation.valid === false) {
      failed.push({ index: i, title: (drafts[i].title ?? "").slice(0, 50), error: new Error(validation.reason) });
      continue;
    }

    const deckWithId: Deck = {
      ...validation.deck,
      id: `deck_${baseId}_${i}`,
    };

    try {
      const c = await createDeck(userId, deckWithId);
      created.push(c);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      failed.push({ index: i, title: validation.deck.title, error });
      console.error(`[createDecksBulk] Module ${i + 1} (${validation.deck.title}) failed:`, error);
    }
  }

  return { created, failed };
}

export async function updateDeckInDB(
  deckId: string,
  updates: Partial<Deck>
): Promise<{ insertedCards?: Card[] } | void> {
  // Update deck metadata
  const deckUpdates: Record<string, unknown> = {};
  if (updates.title !== undefined) deckUpdates.title = updates.title;
  if (updates.lang !== undefined) deckUpdates.lang = updates.lang;
  if (updates.emoji !== undefined) deckUpdates.emoji = updates.emoji;
  if (updates.level !== undefined) deckUpdates.level = updates.level;
  if (updates.deckType !== undefined) deckUpdates.deck_type = updates.deckType;
  const updatesType = (updates as { type?: string }).type;
  if (updatesType !== undefined) deckUpdates.type = updatesType;
  if ((updates as { studyModes?: StudyModes }).studyModes !== undefined) {
    deckUpdates.study_modes = (updates as { studyModes?: StudyModes }).studyModes;
  }

  if (Object.keys(deckUpdates).length > 0) {
    const { error } = await supabase
      .from("decks")
      .update(deckUpdates)
      .eq("id", deckId);
    if (error) throw error;
  }

  // If cards are updated, replace them all
  if (updates.cards !== undefined) {
    // Delete existing cards
    const { error: deleteError } = await supabase
      .from("cards")
      .delete()
      .eq("deck_id", deckId);
    if (deleteError) throw deleteError;

    if (updates.cards.length > 0) {
      const ts = Date.now();
      const cardRows = updates.cards.map((card, i) => ({
        id: `card_${deckId}_${i}_${ts}`,
        deck_id: deckId,
        front: card.front,
        back: card.back,
        front_language: card.frontLang?.trim() || null,
        back_language: card.backLang?.trim() || null,
        hint: card.hint ?? null,
        definition: card.definition ?? null,
        example: card.example ?? null,
        image_url: card.imageUrl ?? null,
        card_type: card.cardType ?? null,
        level: card.level ?? null,
        position: i,
        test_data: card.testData ?? null,
      }));

      await insertCardsRowsInBatches(cardRows);

      const insertedCards: Card[] = updates.cards.map((card, i) => ({
        ...card,
        id: cardRows[i].id,
      }));
      return { insertedCards };
    }
  }
}

export async function deleteDeckFromDB(deckId: string): Promise<void> {
  const idCheck = validateId(deckId, "deckId");
  if (!idCheck.valid) throw new Error(`deleteDeckFromDB: ${idCheck.error}`);

  await supabase.from("cards").delete().eq("deck_id", deckId);
  const { error } = await supabase.from("decks").delete().eq("id", deckId);
  if (error) {
    const classified = classifySupabaseError(error);
    logFlowDiagnostic({
      flow: "library.deleteDeck",
      operation: "delete",
      entity: "deck",
      entityId: deckId,
      success: false,
      error,
      classified,
    });
    throw error;
  }
  logFlowDiagnostic({
    flow: "library.deleteDeck",
    operation: "delete",
    entity: "deck",
    entityId: deckId,
    success: true,
  });
}

// ─── Sharing ──────────────────────────────────────────────────────────────────

/**
 * Enable sharing for a deck. Reuses existing share token if the deck is already shared
 * — never overwrites with a new token. New token is created only when:
 * - deck has never been shared (share_token is null/empty), or
 * - user previously disabled sharing (unshareDeck) and is now re-enabling.
 * This ensures share links persist across page refresh, deploy, re-login.
 */
export async function shareDeck(
  deckId: string,
  shareAccess: "read" | "write" = "read"
): Promise<string> {
  const { data, error: fetchError } = await supabase
    .from("decks")
    .select("share_token")
    .eq("id", deckId)
    .maybeSingle();

  if (fetchError) throw fetchError;

  const existingToken =
    typeof data?.share_token === "string" && data.share_token.trim() !== ""
      ? data.share_token
      : null;

  if (existingToken) {
    await updateShareAccess(deckId, shareAccess);
    return existingToken;
  }

  const token = generateShareToken();
  const { error } = await supabase
    .from("decks")
    .update({ share_token: token, share_access: shareAccess })
    .eq("id", deckId);

  if (error) throw error;
  return token;
}

/** Update only the share_access level for an already-shared deck */
export async function updateShareAccess(
  deckId: string,
  shareAccess: "read" | "write"
): Promise<void> {
  const { error } = await supabase
    .from("decks")
    .update({ share_access: shareAccess })
    .eq("id", deckId);
  if (error) throw error;
}

/** Remove the share token (make deck private again) */
export async function unshareDeck(deckId: string): Promise<void> {
  const { error } = await supabase
    .from("decks")
    .update({ share_token: null })
    .eq("id", deckId);
  if (error) throw error;
}

// ─── Deck mode progress (cards, learn, test, games) ───────────────────────────

export type DeckModeKey = "cards" | "learn" | "test" | "games" | "philology";

export interface DeckModeProgress {
  cards: number;
  learn: number;
  test: number;
  games: number;
  philology: number;
}

const DEFAULT_MODE_PROGRESS: DeckModeProgress = { cards: 0, learn: 0, test: 0, games: 0, philology: 0 };

/** Fetch mode-specific progress for a deck. Returns 0-100 per mode. Fails gracefully if table is missing. */
export async function fetchDeckModeProgress(
  userId: string,
  deckId: string
): Promise<DeckModeProgress> {
  try {
    const { data, error } = await supabase
      .from("deck_mode_progress")
      .select("mode, progress_pct")
      .eq("user_id", userId)
      .eq("deck_id", deckId);

    if (error) {
      const msg = String(error.message ?? "");
      if (error.code === "PGRST205" || msg.includes("schema cache") || msg.includes("Could not find the table") || msg.includes("deck_mode_progress")) {
        console.warn("[fetchDeckModeProgress] Table may not exist — run supabase-migration-deck-mode-progress.sql in Supabase SQL Editor.");
      } else {
        console.warn("[fetchDeckModeProgress]", error);
      }
      return { ...DEFAULT_MODE_PROGRESS };
    }

    const result = { ...DEFAULT_MODE_PROGRESS };
    for (const row of data ?? []) {
      const mode = row.mode as DeckModeKey;
      if (mode in result) result[mode] = Math.min(100, Math.max(0, row.progress_pct ?? 0));
    }
    return result;
  } catch (err) {
    console.warn("[fetchDeckModeProgress] Failed:", err);
    return { ...DEFAULT_MODE_PROGRESS };
  }
}

/** Update progress for ONE mode only. Does not overwrite other modes. Fails gracefully if table is missing. */
export async function upsertDeckModeProgress(
  userId: string,
  deckId: string,
  mode: DeckModeKey,
  progressPct: number
): Promise<void> {
  const pct = Math.min(100, Math.max(0, Math.round(progressPct)));
  try {
    const { error } = await supabase.from("deck_mode_progress").upsert(
      {
        user_id: userId,
        deck_id: deckId,
        mode,
        progress_pct: pct,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,deck_id,mode" }
    );
    if (error) {
      const msg = String(error.message ?? "");
      if (error.code === "PGRST205" || msg.includes("schema cache") || msg.includes("Could not find the table") || msg.includes("deck_mode_progress")) {
        console.warn("[upsertDeckModeProgress] Table may not exist — run supabase-migration-deck-mode-progress.sql in Supabase SQL Editor.");
      } else {
        console.warn("[upsertDeckModeProgress]", error);
      }
    }
  } catch (err) {
    console.warn("[upsertDeckModeProgress] Failed:", err);
  }
}

// ─── Per-game progress (match, tower, defender, etc.) ─────────────────────────

export type GameId = string;

/** Fetch per-game progress for a deck. Returns { [gameId]: 0-100 }. Fails gracefully if table is missing. */
export async function fetchDeckGameProgress(
  userId: string,
  deckId: string
): Promise<Record<string, number>> {
  try {
    const { data, error } = await supabase
      .from("deck_game_progress")
      .select("game_id, progress_pct")
      .eq("user_id", userId)
      .eq("deck_id", deckId);

    if (error) {
      const msg = String(error.message ?? "");
      if (error.code === "PGRST205" || msg.includes("schema cache") || msg.includes("Could not find the table") || msg.includes("deck_game_progress")) {
        console.warn("[fetchDeckGameProgress] Table may not exist — using localStorage fallback. Run supabase-migration-deck-game-progress.sql for persistence.");
      } else {
        console.warn("[fetchDeckGameProgress]", error);
      }
      return {};
    }

    const result: Record<string, number> = {};
    for (const row of data ?? []) {
      const pct = Math.min(100, Math.max(0, row.progress_pct ?? 0));
      result[row.game_id as string] = pct;
    }
    return result;
  } catch (err) {
    console.warn("[fetchDeckGameProgress] Failed:", err);
    return {};
  }
}

/** Update progress for ONE game only. Uses MAX(existing, new) to preserve partial progress. Fails gracefully if table is missing. */
export async function upsertDeckGameProgress(
  userId: string,
  deckId: string,
  gameId: string,
  progressPct: number
): Promise<void> {
  const newPct = Math.min(100, Math.max(0, Math.round(progressPct)));
  try {
    const { data: existing, error: fetchErr } = await supabase
      .from("deck_game_progress")
      .select("progress_pct")
      .eq("user_id", userId)
      .eq("deck_id", deckId)
      .eq("game_id", gameId)
      .maybeSingle();

    if (fetchErr) {
      const msg = String(fetchErr.message ?? "");
      if (fetchErr.code === "PGRST205" || msg.includes("schema cache") || msg.includes("Could not find the table") || msg.includes("deck_game_progress")) {
        console.warn("[upsertDeckGameProgress] Table may not exist — data saved to localStorage. Run supabase-migration-deck-game-progress.sql for persistence.");
      }
      return;
    }

    const currentPct = (existing as { progress_pct?: number } | null)?.progress_pct ?? 0;
    const pct = Math.max(currentPct, newPct);

    const { error } = await supabase.from("deck_game_progress").upsert(
      {
        user_id: userId,
        deck_id: deckId,
        game_id: gameId,
        progress_pct: pct,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,deck_id,game_id" }
    );
    if (error) {
      const msg = String(error.message ?? "");
      if (error.code === "PGRST205" || msg.includes("schema cache") || msg.includes("Could not find the table") || msg.includes("deck_game_progress")) {
        console.warn("[upsertDeckGameProgress] Table may not exist — data saved to localStorage.");
      } else {
        console.warn("[upsertDeckGameProgress]", error);
      }
    }
  } catch (err) {
    console.warn("[upsertDeckGameProgress] Failed:", err);
  }
}

// ─── Folders ─────────────────────────────────────────────────────────────────

/** Batch-fetch share tokens from folder_shares. Returns Map<folderId, share_token>. */
async function fetchFolderShareTokensBatch(folderIds: string[]): Promise<Map<string, string>> {
  if (folderIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from("folder_shares")
    .select("folder_id, share_token")
    .in("folder_id", folderIds);

  if (error) {
    if (isMissingRelationOrColumnError(error)) return new Map();
    throw error;
  }

  const map = new Map<string, string>();
  for (const row of data ?? []) {
    if (typeof row.share_token === "string" && row.share_token.trim()) {
      map.set(row.folder_id, row.share_token);
    }
  }
  return map;
}

export async function fetchFolders(userId: string): Promise<Folder[]> {
  const { data, error } = await supabase
    .from("folders")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  const rows = data ?? [];
  const shareTokens = await fetchFolderShareTokensBatch(rows.map((r: { id: string }) => r.id));

  return rows.map((row) => {
    const token = shareTokens.get(row.id) ?? (typeof row.share_token === "string" && row.share_token.trim() ? row.share_token : null);
    return mapFolderRow(row, token);
  });
}

export async function createFolder(userId: string, title: string): Promise<Folder> {
  const uidCheck = validateNonEmptyString(userId, "userId");
  if (!uidCheck.valid) throw new Error(`createFolder: ${uidCheck.error}`);
  const titleCheck = validateNonEmptyString(title, "title");
  if (!titleCheck.valid) throw new Error(`createFolder: ${titleCheck.error}`);

  const { data, error } = await supabase
    .from("folders")
    .insert({ user_id: userId, title })
    .select()
    .single();

  if (error) {
    const classified = classifySupabaseError(error);
    logFlowDiagnostic({
      flow: "folder.createFolder",
      operation: "insert",
      entity: "folder",
      userId,
      success: false,
      error,
      classified,
    });
    throw error;
  }

  const folder = mapFolderRow(data);
  logFlowDiagnostic({
    flow: "folder.createFolder",
    operation: "insert",
    entity: "folder",
    userId,
    entityId: folder.id,
    success: true,
  });
  return folder;
}

export async function fetchFolder(userId: string, folderId: string): Promise<Folder | null> {
  const { data, error } = await supabase
    .from("folders")
    .select("id, user_id, title, created_at, tags")
    .eq("id", folderId)
    .eq("user_id", userId)
    .single();

  if (error || !data) return null;
  return mapFolderRow(data);
}

/** Fetch folder by ID only (for shared links — no user filter) */
export async function fetchFolderById(folderId: string): Promise<Folder | null> {
  const { data, error } = await supabase
    .from("folders")
    .select("*")
    .eq("id", folderId)
    .single();

  if (error) {
    console.error("fetchFolderById error:", { folderId, error });
    return null;
  }
  if (!data) return null;
  return mapFolderRow(data);
}

export async function fetchFolderShareToken(folderId: string): Promise<string | null> {
  const shareTableToken = await fetchFolderShareTokenFromTable(folderId);
  if (shareTableToken !== undefined) {
    return shareTableToken;
  }

  const legacyToken = await fetchLegacyFolderShareToken(folderId);
  return legacyToken ?? null;
}

/**
 * Enable sharing for a folder. Reuses existing share token if the folder is already shared
 * — never overwrites with a new token. New token is created only when:
 * - folder has never been shared (no token in folder_shares or folders.share_token), or
 * - user previously disabled sharing (unshareFolder) and is now re-enabling.
 * This ensures share links persist across page refresh, deploy, re-login.
 */
export async function shareFolder(folderId: string): Promise<string> {
  const tableToken = await fetchFolderShareTokenFromTable(folderId);
  let folderToken =
    typeof tableToken === "string" && tableToken.trim()
      ? tableToken
      : null;

  if (!folderToken) {
    const legacyToken = await fetchLegacyFolderShareToken(folderId);
    folderToken =
      typeof legacyToken === "string" && legacyToken.trim()
        ? legacyToken
        : generateShareToken();
  }

  const persistedInShareTable = await upsertFolderShareTokenInTable(folderId, folderToken);
  if (!persistedInShareTable) {
    const persistedLegacyToken = await updateLegacyFolderShareToken(folderId, folderToken);
    if (!persistedLegacyToken) {
      throw new Error("FOLDER_SHARING_NOT_CONFIGURED");
    }
  }

  const deckIds = await fetchFolderDeckIds(folderId);
  try {
    await ensureDeckShareTokens(deckIds);
  } catch (deckErr) {
    // Best-effort: folder share works via folder_shares RLS without deck share_token.
    // Decks in shared folders are readable via "Public can read decks from folder shares".
    // Skip if we can't update deck tokens (e.g. decks owned by other users).
    console.warn("[shareFolder] Could not ensure deck share tokens:", deckErr);
  }

  return folderToken;
}

/** Remove the share token (make folder private again) */
export async function unshareFolder(folderId: string): Promise<void> {
  const { error: deleteError } = await supabase
    .from("folder_shares")
    .delete()
    .eq("folder_id", folderId);

  if (deleteError && !isMissingRelationOrColumnError(deleteError)) {
    throw deleteError;
  }

  const { error: updateError } = await supabase
    .from("folders")
    .update({ share_token: null })
    .eq("id", folderId);

  if (updateError) {
    if (isMissingRelationOrColumnError(updateError)) return;
    throw updateError;
  }
}

export async function fetchSharedFolder(
  token: string
): Promise<{ folder: Folder; decks: Deck[] } | null> {
  const tableResult = await fetchSharedFolderFromShareTable(token);
  if (tableResult) {
    return tableResult;
  }

  const { data: folderRow, error: folderError } = await supabase
    .from("folders")
    .select("id, user_id, title, created_at, tags")
    .eq("share_token", token)
    .single();

  if (folderError && isMissingRelationOrColumnError(folderError)) {
    return null;
  }
  if (folderError || !folderRow) {
    return null;
  }

  const deckIds = await fetchFolderDeckIds(folderRow.id);
  const decks = deckIds.length > 0 ? await fetchDecksByIds(deckIds) : [];

  return {
    folder: mapFolderRow(folderRow, token),
    decks,
  };
}

/** Fetch shared folder with full deck cards for study. Ensures all decks have cards loaded. */
export async function fetchSharedFolderWithCards(
  token: string
): Promise<{ folder: Folder; decks: Deck[] } | null> {
  const result = await fetchSharedFolder(token);
  if (!result || result.decks.length === 0) return result;
  const needsCards = result.decks.some((d) => !d.cards?.length);
  if (!needsCards) return result;
  const deckIds = result.decks.map((d) => d.id);
  const decksWithCards = await fetchDecksByIds(deckIds);
  const orderMap = new Map(deckIds.map((id, i) => [id, i]));
  decksWithCards.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
  return { folder: result.folder, decks: decksWithCards };
}

/** Flatten all study-ready cards from folder decks into one ordered list. */
export function buildFolderStudyCards(decks: Deck[]): Card[] {
  const cards: Card[] = [];
  for (const deck of decks) {
    const deckCards = deck.cards ?? [];
    for (const c of deckCards) {
      if (c?.front != null && (typeof c.front === "string" ? c.front.trim() : c.front) !== "") {
        cards.push(c);
      }
    }
  }
  return cards;
}

/** Fetch decks by IDs (for shared folder — decks may belong to another user) */
export async function fetchDecksByIds(deckIds: string[]): Promise<Deck[]> {
  if (deckIds.length === 0) return [];

  const fetchBatch = async (selectCols: string) => {
    const allDecksData: DecksRow[] = [];
    for (let i = 0; i < deckIds.length; i += DECK_ID_QUERY_BATCH_SIZE) {
      const batch = deckIds.slice(i, i + DECK_ID_QUERY_BATCH_SIZE);
      const { data: decksData, error: decksError } = await supabase
        .from("decks")
        .select(selectCols)
        .in("id", batch)
        .order("created_at", { ascending: true })
        .order("id", { ascending: true });

      if (decksError) throw decksError;
      if (decksData?.length) allDecksData.push(...(decksData as DecksRow[]));
    }
    return allDecksData;
  };

  let allDecksData: DecksRow[];
  try {
    allDecksData = await fetchBatch(DECK_SELECT_COLUMNS);
  } catch (e) {
    if (isMissingRelationOrColumnError(e as { code?: string; message?: string })) {
      allDecksData = await fetchBatch(DECK_SELECT_COLUMNS_SAFE);
    } else {
      throw e;
    }
  }
  if (allDecksData.length === 0) return [];

  const orderMap = new Map(deckIds.map((id, idx) => [id, idx]));
  allDecksData.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));

  const cardRows = await fetchAllCardsForDeckIds(allDecksData.map((d) => d.id));
  return mapDeckRowsWithCards(allDecksData, cardRows);
}

/** Fetch a single deck by ID (for edit page). Returns null if not found. */
export async function fetchDeckById(deckId: string): Promise<Deck | null> {
  const decks = await fetchDecksByIds([deckId]);
  return decks[0] ?? null;
}

/**
 * Fetch cards for a deck directly from Supabase (CRITICAL for TTS after Create and Study).
 * Returns fully hydrated cards with DB ids, front_language, back_language, etc.
 */
export async function fetchCardsByDeckId(deckId: string): Promise<Card[]> {
  const rows = await fetchAllCardsForDeckIds([deckId]);
  return rows.map((row) => mapCardRow(row));
}

/** Update folder tags */
export async function updateFolderTags(folderId: string, tags: string[]): Promise<void> {
  const { error } = await supabase
    .from("folders")
    .update({ tags })
    .eq("id", folderId);
  if (error) throw error;
}

/** Fetch deck IDs that belong to a folder. Returns [] if table missing. */
export async function fetchFolderDeckIds(folderId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("folder_decks")
    .select("deck_id")
    .eq("folder_id", folderId);

  if (error) {
    if (isMissingRelationOrColumnError(error)) {
      console.warn("[fetchFolderDeckIds] Table folder_decks may not exist:", error.message);
      return [];
    }
    throw error;
  }
  return (data ?? []).map((r) => r.deck_id);
}

const ADD_TO_FOLDER_BATCH = 100;

/** Add decks to a folder. Uses RPC when available; falls back to direct insert. Skips already-in-folder decks. Batches for large lists. */
export async function addDecksToFolder(
  folderId: string,
  deckIds: string[]
): Promise<void> {
  if (!folderId || folderId.trim() === "" || deckIds.length === 0) return;

  const existing = await fetchFolderDeckIds(folderId);
  const existingSet = new Set(existing);
  const toAdd = deckIds.filter((id) => !existingSet.has(id));
  if (toAdd.length === 0) return;

  for (let i = 0; i < toAdd.length; i += ADD_TO_FOLDER_BATCH) {
    const batch = toAdd.slice(i, i + ADD_TO_FOLDER_BATCH);
    const rows = batch.map((deck_id) => ({ folder_id: folderId, deck_id }));

    let { error } = await supabase.rpc("add_decks_to_folder", {
      p_folder_id: folderId,
      p_deck_ids: batch,
    });

    if (error) {
      const msg = String(error.message ?? "");
      const code = (error as { code?: string }).code;
      const rpcMissing =
        code === "42883" ||
        /function.*does not exist|add_decks_to_folder.*not found|could not find/i.test(msg);
      if (rpcMissing) {
        ({ error } = await supabase.from("folder_decks").insert(rows));
      }
    }

    if (error) {
      const err = error as { code?: string; message?: string; details?: string };
      inspectQueryError({
        queryName: "addDecksToFolder",
        flow: "folder.addDecksToFolder",
        table: "folder_decks",
        filters: { folderId, deckCount: batch.length, batchIndex: i / ADD_TO_FOLDER_BATCH },
        responseError: error,
      });
      console.error("[addDecksToFolder] Failed:", { code: err.code, message: err.message, details: err.details });
      throw error;
    }
  }
}

/** Remove multiple decks from a folder */
export async function removeDecksFromFolder(
  folderId: string,
  deckIds: string[]
): Promise<void> {
  if (!folderId || folderId.trim() === "" || deckIds.length === 0) return;
  const { error } = await supabase
    .from("folder_decks")
    .delete()
    .eq("folder_id", folderId)
    .in("deck_id", deckIds);
  if (error) throw error;
}

/** Remove a deck from a folder */
export async function removeDeckFromFolder(
  folderId: string,
  deckId: string
): Promise<void> {
  const { error } = await supabase
    .from("folder_decks")
    .delete()
    .eq("folder_id", folderId)
    .eq("deck_id", deckId);
  if (error) throw error;
}

export interface DeckActivitySignal {
  lastStudiedAt: string | null;
  progressPct: number;
}

/** Fetch per-deck recent study signal for sorting and folder summaries. */
export async function fetchDeckActivitySignals(
  userId: string,
  deckIds: string[]
): Promise<Record<string, DeckActivitySignal>> {
  if (!userId || deckIds.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from("deck_mode_progress")
      .select("deck_id, updated_at, progress_pct")
      .eq("user_id", userId)
      .in("deck_id", deckIds);

    if (error) {
      const msg = String(error.message ?? "");
      if (error.code === "PGRST205" || msg.includes("schema cache") || msg.includes("Could not find the table") || msg.includes("deck_mode_progress")) {
        console.warn("[fetchDeckActivitySignals] Table may not exist — returning empty activity signals.");
      } else {
        console.warn("[fetchDeckActivitySignals]", error);
      }
      return {};
    }

    const result: Record<string, DeckActivitySignal> = {};
    for (const row of data ?? []) {
      const deckId = String(row.deck_id ?? "");
      if (!deckId) continue;
      const updatedAt = typeof row.updated_at === "string" ? row.updated_at : null;
      const progressPct = Math.min(100, Math.max(0, Number(row.progress_pct) || 0));
      const existing = result[deckId];
      result[deckId] = {
        lastStudiedAt:
          !existing?.lastStudiedAt ||
          (updatedAt && new Date(updatedAt).getTime() > new Date(existing.lastStudiedAt).getTime())
            ? updatedAt
            : existing.lastStudiedAt,
        progressPct: Math.max(existing?.progressPct ?? 0, progressPct),
      };
    }
    return result;
  } catch (err) {
    console.warn("[fetchDeckActivitySignals] Failed:", err);
    return {};
  }
}

/** Update folder title */
export async function updateFolder(
  folderId: string,
  updates: { title: string }
): Promise<void> {
  const { error } = await supabase
    .from("folders")
    .update({ title: updates.title })
    .eq("id", folderId);
  if (error) throw error;
}

/** Delete a folder (CASCADE clears folder_decks) */
export async function deleteFolder(folderId: string): Promise<void> {
  const { error } = await supabase.from("folders").delete().eq("id", folderId);
  if (error) throw error;
}

// ─── Teacher Library ─────────────────────────────────────────────────────────

/** Mark teacher's library as public so anyone with the link can view their decks */
export async function setLibraryPublic(userId: string): Promise<void> {
  const { error } = await supabase
    .from("public_teacher_libraries")
    .upsert({ user_id: userId });
  if (error) throw error;
}

/** Fetch all decks belonging to a teacher (for public library page, no auth required) */
export async function fetchTeacherDecks(teacherId: string): Promise<Deck[]> {
  const deckRows = await fetchAllDecksRowsForUser(teacherId);
  if (deckRows.length === 0) return [];

  const cardRows = await fetchAllCardsForDeckIds(deckRows.map((deck) => deck.id));
  return mapDeckRowsWithCards(deckRows, cardRows);
}

/** Fetch a single deck from a teacher's library (for study pages) */
export async function fetchTeacherDeck(
  teacherId: string,
  deckId: string
): Promise<Deck | null> {
  const { data: deckData, error: deckError } = await supabase
    .from("decks")
    .select("*")
    .eq("id", deckId)
    .eq("user_id", teacherId)
    .single();

  if (deckError || !deckData) return null;

  const cardsData = await fetchAllCardsForDeckIds([deckId]);

  return {
    id: deckData.id,
    title: deckData.title,
    lang: deckData.lang,
    emoji: deckData.emoji,
    level: deckData.level ?? undefined,
    type: ((deckData as { type?: string }).type as "words" | "cards" | "tests") ?? "cards",
    deckType: deckData.deck_type ?? undefined,
    shareToken: deckData.share_token ?? null,
    shareAccess: (deckData.share_access as "read" | "write") ?? "read",
    studyModes: parseStudyModes((deckData as { study_modes?: StudyModes | string | null }).study_modes),
    created_at: (deckData as { created_at?: string }).created_at,
    cards: cardsData.map((c) => mapCardRow(c)),
  };
}

/** Add cards to an existing deck (for Smart Sync). Inserts in batches. */
async function addCardsToDeck(
  deckId: string,
  cards: Card[],
  startPosition: number
): Promise<void> {
  if (cards.length === 0) return;

  const ts = Date.now();
  const cardRows = cards.map((card, i) => ({
    id: `card_${deckId}_${ts}_${i}`,
    deck_id: deckId,
    front: card.front,
    back: card.back,
    front_language: card.frontLang?.trim() || null,
    back_language: card.backLang?.trim() || null,
    hint: card.hint ?? null,
    definition: card.definition ?? null,
    example: card.example ?? null,
    image_url: card.imageUrl ?? null,
    card_type: (card as { cardType?: string }).cardType ?? null,
    level: (card as { level?: string }).level ?? null,
    position: startPosition + i,
    test_data: (card as { testData?: unknown }).testData ?? null,
  }));

  await insertCardsRowsInBatches(cardRows);
}

export type SyncResult = { decksAdded: number; cardsAdded: number };

/** Force re-add all teacher decks into the student's account. Uses sourceDeckId to prevent duplicates. */
export async function forceReaddAllDecks(
  teacherId: string,
  studentId: string
): Promise<SyncResult> {
  const teacherDecks = await fetchTeacherDecks(teacherId);
  let decksAdded = 0;

  for (const deck of teacherDecks) {
    try {
      await createDeck(
        studentId,
        {
          title: deck.title,
          lang: deck.lang,
          emoji: deck.emoji,
          level: deck.level,
          deckType: deck.deckType,
          studyModes: deck.studyModes ?? DEFAULT_STUDY_MODES,
          cards: deck.cards,
        },
        { sourceDeckId: deck.id }
      );
      decksAdded++;
    } catch (err) {
      if (err instanceof DeckAlreadyImportedError) {
        /* already have it, skip */
      } else throw err;
    }
  }

  const totalCards = teacherDecks.reduce((sum, d) => sum + d.cards.length, 0);
  return { decksAdded, cardsAdded: totalCards };
}

/** Sync decks from a shared folder into the student's account. Uses deck IDs/cards already loaded
 * (fetchDecksByIds or fetchGlobalDeckForStudy), so no fetchTeacherDecks — avoids RLS issues.
 * Optional onProgress(current, total) is called as each deck is processed (0..total). */
export async function smartSyncSharedFolderDecks(
  folderDecks: Deck[],
  studentId: string,
  onProgress?: (current: number, total: number) => void
): Promise<SyncResult> {
  if (!folderDecks?.length) return { decksAdded: 0, cardsAdded: 0 };

  const [fullDecks, studentDecks] = await Promise.all([
    Promise.all(
      folderDecks.map(async (d) => {
        if (d.cards?.length) return d;
        const full = await fetchGlobalDeckForStudy(d.id);
        if (full?.cards?.length) return full;
        const byIds = await fetchDecksByIds([d.id]);
        return byIds[0] ?? d;
      })
    ),
    fetchDecks(studentId),
  ]);

  const total = fullDecks.length;
  onProgress?.(0, total);

  const studentByTitle = new Map<string, Deck>();
  for (const d of studentDecks) {
    studentByTitle.set(d.title, d);
  }

  let decksAdded = 0;
  let cardsAdded = 0;
  let processed = 0;

  for (const sourceDeck of fullDecks) {
    const alreadyImported = await userAlreadyHasDeckFromSource(studentId, sourceDeck.id);
    const studentDeck = alreadyImported
      ? await fetchStudentDeckBySourceId(studentId, sourceDeck.id)
      : studentByTitle.get(sourceDeck.title);

    if (!studentDeck) {
      try {
        await createDeck(studentId, {
          title: sourceDeck.title,
          lang: sourceDeck.lang,
          emoji: sourceDeck.emoji,
          level: sourceDeck.level,
          deckType: sourceDeck.deckType,
          studyModes: sourceDeck.studyModes ?? DEFAULT_STUDY_MODES,
          cards: sourceDeck.cards ?? [],
        }, { sourceDeckId: sourceDeck.id });
        decksAdded++;
      } catch (err) {
        if (err instanceof DeckAlreadyImportedError) {
          /* already have it, skip */
        } else throw err;
      }
      processed++;
      onProgress?.(processed, total);
      continue;
    }

    const existingFronts = new Set(
      (studentDeck.cards ?? []).map((c) => c.front?.trim?.()?.toLowerCase?.() ?? "")
    );
    const newCards = (sourceDeck.cards ?? []).filter(
      (c) => !existingFronts.has((c.front?.trim?.() ?? "").toLowerCase())
    );
    if (newCards.length === 0) {
      processed++;
      onProgress?.(processed, total);
      continue;
    }

    const startPosition = (studentDeck.cards ?? []).length;
    await addCardsToDeck(studentDeck.id, newCards, startPosition);
    cardsAdded += newCards.length;
    processed++;
    onProgress?.(processed, total);
  }

  return { decksAdded, cardsAdded };
}

/** Force-add all decks from shared folder. Same as smartSync but always tries create; skips on DeckAlreadyImportedError. */
export async function forceReaddSharedFolderDecks(
  folderDecks: Deck[],
  studentId: string,
  onProgress?: (current: number, total: number) => void
): Promise<SyncResult> {
  return smartSyncSharedFolderDecks(folderDecks, studentId, onProgress);
}

/** Smart Sync: deep compare to avoid duplicates. Match decks by title, cards by front text. */
export async function smartSyncLibrary(
  teacherId: string,
  studentId: string
): Promise<SyncResult> {
  const [teacherDecks, studentDecks] = await Promise.all([
    fetchTeacherDecks(teacherId),
    fetchDecks(studentId),
  ]);

  const studentDecksByTitle = new Map<string, Deck>();
  for (const d of studentDecks) {
    studentDecksByTitle.set(d.title, d);
  }

  let decksAdded = 0;
  let cardsAdded = 0;

  for (const teacherDeck of teacherDecks) {
    const studentDeck = studentDecksByTitle.get(teacherDeck.title);

    if (!studentDeck) {
      await createDeck(studentId, {
        title: teacherDeck.title,
        lang: teacherDeck.lang,
        emoji: teacherDeck.emoji,
        level: teacherDeck.level,
        deckType: teacherDeck.deckType,
        studyModes: teacherDeck.studyModes ?? DEFAULT_STUDY_MODES,
        cards: teacherDeck.cards,
      });
      decksAdded++;
      continue;
    }

    const existingFronts = new Set(
      studentDeck.cards.map((c) => c.front.trim().toLowerCase())
    );
    const newCards = teacherDeck.cards.filter(
      (c) => !existingFronts.has(c.front.trim().toLowerCase())
    );

    if (newCards.length === 0) continue;

    const startPosition = studentDeck.cards.length;
    await addCardsToDeck(studentDeck.id, newCards, startPosition);
    cardsAdded += newCards.length;
  }

  return { decksAdded, cardsAdded };
}

/** Fetch a deck by its public share token (no auth required — uses anon key) */
export async function fetchSharedDeck(token: string): Promise<Deck | null> {
  const normalizedToken = normalizeSharedToken(token);
  if (!normalizedToken) return null;

  const fetchSharedDeckRow = async (column: "share_token" | "id"): Promise<DecksRow | null> => {
    try {
      const { data, error } = await supabase
        .from("decks")
        .select(DECK_SELECT_COLUMNS)
        .eq(column, normalizedToken)
        .maybeSingle();

      if (error) throw error;
      return (data as DecksRow | null) ?? null;
    } catch (error) {
      if (!isMissingRelationOrColumnError(error as { code?: string | number; message?: string })) {
        throw error;
      }

      const { data, error: safeError } = await supabase
        .from("decks")
        .select(DECK_SELECT_COLUMNS_SAFE)
        .eq(column, normalizedToken)
        .maybeSingle();

      if (safeError) throw safeError;
      return (data as DecksRow | null) ?? null;
    }
  };

  let deckData = await fetchSharedDeckRow("share_token");
  if (!deckData && normalizedToken.startsWith("deck_")) {
    deckData = await fetchSharedDeckRow("id");
  }

  if (!deckData) return null;

  const publicShareToken =
    typeof deckData.share_token === "string" && deckData.share_token.trim() !== ""
      ? deckData.share_token.trim()
      : null;
  if (!publicShareToken) return null;

  const cardsData = await fetchAllCardsForDeckIds([deckData.id]);

  return {
    id: deckData.id,
    title: deckData.title,
    lang: deckData.lang,
    emoji: deckData.emoji,
    level: deckData.level ?? undefined,
    type: ((deckData as { type?: string }).type as "words" | "cards" | "tests") ?? "cards",
    deckType: deckData.deck_type ?? undefined,
    shareToken: publicShareToken,
    shareAccess: (deckData.share_access as "read" | "write") ?? "read",
    studyModes: parseStudyModes((deckData as { study_modes?: StudyModes | string | null }).study_modes),
    created_at: deckData.created_at ?? undefined,
    cards: cardsData.map((c) => mapCardRow(c)),
  };
}

/** Update card SRS fields after a review (SM-2 algorithm) */
export async function updateCardSrs(
  cardId: string,
  data: { repetition_count: number; easiness_factor: number; interval_days: number; next_review: string }
): Promise<void> {
  const { error } = await supabase
    .from("cards")
    .update({
      repetition_count: data.repetition_count,
      easiness_factor: data.easiness_factor,
      interval_days: data.interval_days,
      next_review: data.next_review,
    })
    .eq("id", cardId);
  if (error) throw error;
}

/** Update a single card's content (front, back, image, audio, enrichment). Preserves card id. */
export async function updateCardInDB(
  cardId: string,
  updates: {
    front?: string;
    back?: string;
    hint?: string | null;
    definition?: string | null;
    example?: string | null;
    imageUrl?: string | null;
    frontLang?: string | null;
    backLang?: string | null;
    frontAudioUrl?: string | null;
    backAudioUrl?: string | null;
    frontAudioLocale?: string | null;
    backAudioLocale?: string | null;
  }
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (updates.front !== undefined) payload.front = updates.front;
  if (updates.back !== undefined) payload.back = updates.back;
  if (updates.hint !== undefined) payload.hint = updates.hint ?? null;
  if (updates.definition !== undefined) payload.definition = updates.definition ?? null;
  if (updates.example !== undefined) payload.example = updates.example ?? null;
  if (updates.imageUrl !== undefined) payload.image_url = updates.imageUrl ?? null;
  if (updates.frontLang !== undefined) payload.front_language = updates.frontLang?.trim() || null;
  if (updates.backLang !== undefined) payload.back_language = updates.backLang?.trim() || null;
  if (updates.frontAudioUrl !== undefined) payload.front_audio_url = updates.frontAudioUrl ?? null;
  if (updates.backAudioUrl !== undefined) payload.back_audio_url = updates.backAudioUrl ?? null;
  if (updates.frontAudioLocale !== undefined) payload.front_audio_locale = updates.frontAudioLocale ?? null;
  if (updates.backAudioLocale !== undefined) payload.back_audio_locale = updates.backAudioLocale ?? null;
  if (Object.keys(payload).length === 0) return;

  const { data, error } = await supabase
    .from("cards")
    .update(payload)
    .eq("id", cardId)
    .select("id");
  if (error) throw error;
  const updated = Array.isArray(data) ? data : data ? [data] : [];
  if (updated.length === 0) {
    const err = new Error("Card update affected no rows (RLS or missing row)");
    (err as Error & { code?: string }).code = "CARD_UPDATE_NO_ROW";
    throw err;
  }
}

/** Count cards due for review (next_review <= now) across user's decks */
export async function fetchDueCardsCount(userId: string): Promise<number> {
  const { data: decksData } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId);
  if (!decksData?.length) return 0;

  const deckIds = decksData.map((d) => d.id);
  const nowIso = new Date().toISOString();

  const { count, error } = await supabase
    .from("cards")
    .select("*", { count: "exact", head: true })
    .in("deck_id", deckIds)
    .or(`next_review.is.null,next_review.lte."${nowIso}"`);

  if (error) {
    // Column may not exist if SRS migration not run
    if (error.code === "42703") return 0;
    throw error;
  }
  return count ?? 0;
}

export interface DashboardCollectionCounts {
  deckCount: number;
  cardCount: number;
}

/** Exact deck/card totals for Statistics without downloading every row. */
export async function fetchDashboardCollectionCounts(userId: string): Promise<DashboardCollectionCounts> {
  const { data: decksData, error: deckError } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId);

  if (deckError) throw deckError;

  const deckIds = (decksData ?? []).map((deck) => deck.id);
  if (deckIds.length === 0) {
    return {
      deckCount: 0,
      cardCount: 0,
    };
  }

  let cardCount = 0;
  const batchSize = 200;
  for (let i = 0; i < deckIds.length; i += batchSize) {
    const batch = deckIds.slice(i, i + batchSize);
    const { count, error: cardError } = await supabase
      .from("cards")
      .select("id", { count: "exact", head: true })
      .in("deck_id", batch);

    if (cardError) throw cardError;
    cardCount += count ?? 0;
  }

  return {
    deckCount: deckIds.length,
    cardCount,
  };
}

/** SRS statistics: cards grouped for charts. Fetches from Supabase. */
export interface SrsStatsData {
  upcomingByDay: { date: string; count: number }[];
  masteryPie: { name: string; value: number }[];
}

export async function fetchSrsStats(userId: string): Promise<SrsStatsData> {
  const { data: decksData } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId);
  if (!decksData?.length) {
    return { upcomingByDay: [], masteryPie: [] };
  }
  const deckIds = decksData.map((d) => d.id);
  const { data: cardsData, error } = await supabase
    .from("cards")
    .select("next_review, repetition_count, interval_days")
    .in("deck_id", deckIds);

  if (error) {
    if (error.code === "42703") return { upcomingByDay: [], masteryPie: [] };
    throw error;
  }
  const cards = cardsData ?? [];

  let newCount = 0;
  let inProgressCount = 0;
  let masteredCount = 0;

  for (const c of cards) {
    const rep = c.repetition_count ?? 0;
    const interval = c.interval_days ?? 0;
    if (rep === 0) newCount++;
    else if (interval >= 21) masteredCount++;
    else inProgressCount++;
  }

  const { computeWeeklyForecast } = await import("@/utils/weeklyForecast");
  const upcomingByDay = computeWeeklyForecast(cards);

  const masteryPie = [
    { name: "new", value: newCount },
    { name: "learning", value: inProgressCount },
    { name: "mastered", value: masteredCount },
  ].filter((x) => x.value > 0);

  return { upcomingByDay, masteryPie };
}

export interface DifficultCard {
  id: string;
  front: string;
  back: string;
  deck_id: string;
  easiness_factor: number;
  repetition_count: number;
  interval_days: number;
  incorrect_count: number;
  status: "new" | "learning" | "mastered";
}

/** Top 10 most difficult cards: lowest easiness_factor, highest times_incorrect */
export async function fetchDifficultCards(userId: string, limit = 10): Promise<DifficultCard[]> {
  const { data: decksData } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId);
  if (!decksData?.length) return [];

  const deckIds = decksData.map((d) => d.id);
  const { data: cardsData, error: cardsErr } = await supabase
    .from("cards")
    .select("id, front, back, deck_id, easiness_factor, repetition_count, interval_days")
    .in("deck_id", deckIds);

  if (cardsErr) return [];

  // Primary: user_card_progress (from recordCardInteraction). Fallback: legacy progress table.
  const incorrectByCard = new Map<string, number>();

  const { data: ucpData } = await supabase
    .from("user_card_progress")
    .select("card_id, times_incorrect")
    .eq("user_id", userId);
  for (const p of ucpData ?? []) {
    incorrectByCard.set(p.card_id, p.times_incorrect ?? 0);
  }

  const { data: progressData } = await supabase
    .from("progress")
    .select("card_id, incorrect_count")
    .eq("user_id", userId);
  for (const p of progressData ?? []) {
    const existing = incorrectByCard.get(p.card_id) ?? 0;
    incorrectByCard.set(p.card_id, Math.max(existing, p.incorrect_count ?? 0));
  }

  const cards = (cardsData ?? []).map((c) => {
    const rep = c.repetition_count ?? 0;
    const interval = c.interval_days ?? 0;
    let status: "new" | "learning" | "mastered" = "new";
    if (rep > 0) status = interval >= 21 ? "mastered" : "learning";

    return {
      id: c.id,
      front: c.front,
      back: c.back,
      deck_id: c.deck_id,
      easiness_factor: c.easiness_factor ?? 2.5,
      repetition_count: rep,
      interval_days: interval,
      incorrect_count: incorrectByCard.get(c.id) ?? 0,
      status,
    };
  });

  cards.sort((a, b) => {
    const scoreA = a.incorrect_count * 20 - a.easiness_factor;
    const scoreB = b.incorrect_count * 20 - b.easiness_factor;
    return scoreB - scoreA;
  });

  return cards.slice(0, limit);
}

/** Upsert study activity for a user on a given date (adds count for heatmap; minutes come from live tracker) */
export async function upsertUserActivity(userId: string, date: string, addCount: number): Promise<void> {
  const id = `ua_${userId}_${date}`;
  const { data: existing } = await supabase
    .from("user_activity")
    .select("id, count, minutes, total_site_minutes")
    .eq("user_id", userId)
    .eq("date", date)
    .maybeSingle();

  const newCount = (existing?.count ?? 0) + addCount;
  const existingMinutes = existing?.minutes ?? 0;
  const existingSite = (existing as { total_site_minutes?: number } | null)?.total_site_minutes ?? 0;
  const payload = {
    id,
    user_id: userId,
    date,
    count: newCount,
    minutes: existingMinutes,
    total_site_minutes: existingSite,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from("user_activity").upsert(payload, {
    onConflict: "user_id,date",
  });
  if (error) {
    if (error.code === "42P01") return; // table doesn't exist yet
    throw error;
  }
}

export interface UserActivityDay {
  count: number;
  minutes: number;
  total_site_minutes: number;
}

function isMissingUserActivityError(error: { code?: string | null; message?: string | null } | null | undefined): boolean {
  const msg = String(error?.message ?? "");
  return (
    error?.code === "42P01" ||
    error?.code === "42703" ||
    error?.code === "PGRST205" ||
    msg.includes("schema cache") ||
    msg.includes("Could not find the table") ||
    msg.includes("user_activity")
  );
}

/** Upsert study time for the current day (adds minutes to existing) */
export async function upsertUserStudyTime(userId: string, date: string, addMinutes: number): Promise<boolean> {
  if (addMinutes <= 0) return true;
  const id = `ua_${userId}_${date}`;
  const { data: existing, error: existingError } = await supabase
    .from("user_activity")
    .select("id, count, minutes, total_site_minutes")
    .eq("user_id", userId)
    .eq("date", date)
    .maybeSingle();

  if (existingError) {
    if (isMissingUserActivityError(existingError)) return false;
    throw existingError;
  }

  const existingCount = existing?.count ?? 0;
  const existingMinutes = existing?.minutes ?? 0;
  const existingSite = (existing as { total_site_minutes?: number } | null)?.total_site_minutes ?? 0;
  const newMinutes = existingMinutes + addMinutes;

  const payload = {
    id,
    user_id: userId,
    date,
    count: existingCount,
    minutes: newMinutes,
    total_site_minutes: existingSite,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from("user_activity").upsert(payload, {
    onConflict: "user_id,date",
  });
  if (error) {
    if (isMissingUserActivityError(error)) return false;
    throw error;
  }
  return true;
}

/** Upsert total site time for the current day (adds to existing). Fails gracefully if user_activity table is missing. */
export async function upsertUserSiteTime(userId: string, date: string, addMinutes: number): Promise<boolean> {
  if (addMinutes <= 0) return true;
  const id = `ua_${userId}_${date}`;
  const { data: existing, error: existingError } = await supabase
    .from("user_activity")
    .select("id, count, minutes, total_site_minutes")
    .eq("user_id", userId)
    .eq("date", date)
    .maybeSingle();

  if (existingError) {
    if (isMissingUserActivityError(existingError)) return false;
    throw existingError;
  }

  const existingCount = existing?.count ?? 0;
  const existingMinutes = existing?.minutes ?? 0;
  const existingSite = (existing as { total_site_minutes?: number } | null)?.total_site_minutes ?? 0;
  const newSite = existingSite + addMinutes;

  const payload = {
    id,
    user_id: userId,
    date,
    count: existingCount,
    minutes: existingMinutes,
    total_site_minutes: newSite,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from("user_activity").upsert(payload, {
    onConflict: "user_id,date",
  });
  if (error) {
    if (isMissingUserActivityError(error)) return false;
    throw error;
  }
  return true;
}

/** Mode keys for time distribution chart */
export type TimeDistributionModeKey = "cards" | "learn" | "test" | "games";

/** Minutes spent per study mode (from user_card_events). Used for Time Distribution block. */
export interface UserActivityByMode {
  cards: number;
  learn: number;
  test: number;
  games: number;
}

/** Fetch time-by-mode from user_card_events. Estimates active time from response_time_ms (fallback 15s per event). Returns zeros if table missing. */
export async function fetchUserActivityByMode(userId: string, days: number = 30): Promise<UserActivityByMode> {
  const out: UserActivityByMode = { cards: 0, learn: 0, test: 0, games: 0 };
  const start = new Date();
  start.setDate(start.getDate() - days);
  const startStr = start.toISOString().slice(0, 10);

  const { data, error } = await supabase
    .from("user_card_events")
    .select("mode, response_time_ms")
    .eq("user_id", userId)
    .gte("occurred_at", startStr + "T00:00:00Z");

  if (error) {
    if (error.code === "42P01" || String(error.message || "").includes("user_card_events")) return out;
    throw error;
  }

  const FALLBACK_MS = 15_000; // 15 sec per interaction if no response_time_ms
  const MIN_MS = 3_000;

  for (const row of data ?? []) {
    const mode = (row.mode ?? "").toLowerCase();
    const key: TimeDistributionModeKey | null =
      mode === "cards" ? "cards"
      : mode === "learn" ? "learn"
      : mode === "test" ? "test"
      : mode === "games" ? "games"
      : null;
    if (!key) continue;

    const ms = row.response_time_ms != null && row.response_time_ms >= 0
      ? Math.max(row.response_time_ms, MIN_MS)
      : FALLBACK_MS;
    out[key] += ms / 60_000; // convert to minutes
  }

  // Round to 1 decimal
  out.cards = Math.round(out.cards * 10) / 10;
  out.learn = Math.round(out.learn * 10) / 10;
  out.test = Math.round(out.test * 10) / 10;
  out.games = Math.round(out.games * 10) / 10;

  return out;
}

/** Fetch activity counts and minutes. Pass "all" for entire history, or a number for last N days. */
export async function fetchUserActivity(userId: string, days: number | "all" = 30): Promise<Record<string, UserActivityDay>> {
  let query = supabase
    .from("user_activity")
    .select("date, count, minutes, total_site_minutes")
    .eq("user_id", userId)
    .order("date", { ascending: true });

  if (days !== "all") {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    query = query
      .gte("date", start.toISOString().slice(0, 10))
      .lte("date", end.toISOString().slice(0, 10));
  }

  const { data, error } = await query;

  if (error) {
    if (isMissingUserActivityError(error)) return {};
    throw error;
  }

  const out: Record<string, UserActivityDay> = {};
  for (const row of data ?? []) {
    const count = row.count ?? 0;
    const r = row as { total_site_minutes?: number };
    out[row.date] = {
      count,
      minutes: row.minutes ?? Math.round(count * 0.5),
      total_site_minutes: r.total_site_minutes ?? 0,
    };
  }
  return out;
}

/** Get deck IDs with their due card counts, for "Start Review" flow */
export async function fetchDecksWithDueCards(userId: string): Promise<{ deckId: string; dueCount: number }[]> {
  const { data: decksData } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId);
  if (!decksData?.length) return [];

  const deckIds = decksData.map((d) => d.id);
  const nowIso = new Date().toISOString();

  const { data: cardsData, error } = await supabase
    .from("cards")
    .select("deck_id")
    .in("deck_id", deckIds)
    .or(`next_review.is.null,next_review.lte."${nowIso}"`);

  if (error) {
    if (error.code === "42703") return [];
    throw error;
  }

  const byDeck = new Map<string, number>();
  for (const c of cardsData ?? []) {
    byDeck.set(c.deck_id, (byDeck.get(c.deck_id) ?? 0) + 1);
  }
  return Array.from(byDeck.entries())
    .filter(([, count]) => count > 0)
    .map(([deckId, dueCount]) => ({ deckId, dueCount }))
    .sort((a, b) => b.dueCount - a.dueCount);
}

/** Get card counts per deck for folder "Repeat" (overdue OR weak, excludes brand-new). Uses user_card_progress. */
export async function fetchFolderReviewCounts(
  userId: string,
  deckIds: string[]
): Promise<Record<string, number>> {
  if (!userId || deckIds.length === 0) return {};
  try {
    const candidates = await getSmartReviewCandidates(userId, { deckIds, preset: "repeat" });
    const byDeck: Record<string, number> = {};
    for (const c of candidates) {
      byDeck[c.deckId] = (byDeck[c.deckId] ?? 0) + 1;
    }
    return byDeck;
  } catch {
    return {};
  }
}

// ─── Progress ─────────────────────────────────────────────────────────────────

export async function upsertProgress(
  userId: string,
  cardId: string,
  correct: boolean
): Promise<void> {
  const { data: existing } = await supabase
    .from("progress")
    .select("*")
    .eq("user_id", userId)
    .eq("card_id", cardId)
    .single();

  if (existing) {
    await supabase
      .from("progress")
      .update({
        correct_count: existing.correct_count + (correct ? 1 : 0),
        incorrect_count: existing.incorrect_count + (correct ? 0 : 1),
        last_reviewed_at: new Date().toISOString(),
      })
      .eq("id", existing.id);
  } else {
    await supabase.from("progress").insert({
      id: `prog_${userId}_${cardId}_${Date.now()}`,
      user_id: userId,
      card_id: cardId,
      correct_count: correct ? 1 : 0,
      incorrect_count: correct ? 0 : 1,
      last_reviewed_at: new Date().toISOString(),
    });
  }
}

export async function fetchProgress(userId: string) {
  const { data, error } = await supabase
    .from("progress")
    .select("*")
    .eq("user_id", userId);
  if (error) throw error;
  return data ?? [];
}

// ─── Profiles ──────────────────────────────────────────────────────────────────

export interface Profile {
  id: string;
  username: string | null;
  birth_date: string | null;
  is_teacher: boolean | null;
  avatar_url: string | null;
}

import { DEFAULT_STATS, type GamificationStats } from "@/stores/useGamificationStore";

/** Gamification data stored in profiles (or separate table) */
export interface GamificationData {
  crystals: number;
  xp: number;
  currentStreak: number;
  lastActivityDate: string | null;
  studyHistory: string[];
  stats: GamificationStats;
  ownedItems: string[];
  equippedItems: string[];
  equippedTheme: string | null;
  equippedCardSkin: string;
}

/** Hyphen/dash placeholders that must be treated as empty (never show as default username). */
const USERNAME_PLACEHOLDERS = ["-", "—", "–", "\u2010", "\u2011", "\u2012", "\u2013", "\u2014", "\u2015"];

function normalizeProfileUsername(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== "string") return null;
  const t = raw.trim();
  if (!t || USERNAME_PLACEHOLDERS.includes(t)) return null;
  return raw;
}

/** Fetch the current user's profile. Returns null if not found.
 * Uses select("*") so missing columns (e.g. avatar_url before migration) don't break the query.
 * Username values that are hyphen/placeholder are normalized to null so they never appear as default. */
export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // no rows
    throw error;
  }
  if (!data) return null;
  const row = data as Record<string, unknown>;
  return {
    id: row.id as string,
    username: normalizeProfileUsername(row.username as string | null | undefined),
    birth_date: (row.birth_date as string | null) ?? null,
    is_teacher: (row.is_teacher as boolean | null) ?? null,
    avatar_url: (row.avatar_url as string | null | undefined) ?? null,
  };
}

/** Update only the profile avatar_url (e.g. after upload or provider sync). */
export async function updateProfileAvatar(userId: string, avatarUrl: string | null): Promise<void> {
  const { error } = await supabase
    .from("profiles")
    .update({ avatar_url: avatarUrl, updated_at: new Date().toISOString() })
    .eq("id", userId);
  if (error) throw error;
}

/** Fetch gamification data from user's profile. Uses extended profile columns. */
export async function fetchGamification(userId: string): Promise<GamificationData | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("crystals, xp, current_streak, last_activity_date, study_history, stats, owned_items, equipped_items, equipped_theme, equipped_card_skin")
    .eq("id", userId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // no rows
    // Column may not exist yet if migration not run
    if (error.code === "42703") return null;
    throw error;
  }

  if (!data) return null;

  const row = data as {
    crystals?: number;
    xp?: number;
    current_streak?: number;
    last_activity_date?: string | null;
    study_history?: string[] | null;
    stats?: GamificationStats | null;
    owned_items?: string[] | null;
    equipped_items?: string[] | null;
    equipped_theme?: string | null;
    equipped_card_skin?: string | null;
  };

  return {
    crystals: Number(row.crystals) ?? 0,
    xp: Number(row.xp) ?? 0,
    currentStreak: Number(row.current_streak) ?? 0,
    lastActivityDate: row.last_activity_date ?? null,
    studyHistory: Array.isArray(row.study_history) ? row.study_history.filter((d): d is string => typeof d === "string") : [],
    stats: row.stats && typeof row.stats === "object" ? { ...DEFAULT_STATS, ...row.stats } : DEFAULT_STATS,
    ownedItems: Array.isArray(row.owned_items) ? row.owned_items : [],
    equippedItems: Array.isArray(row.equipped_items) ? row.equipped_items : [],
    equippedTheme:
      typeof row.equipped_theme === "string" && row.equipped_theme !== "default"
        ? row.equipped_theme
        : null,
    equippedCardSkin: typeof row.equipped_card_skin === "string" ? row.equipped_card_skin : "default",
  };
}

/** Upsert gamification data to user's profile. */
export async function upsertGamification(
  userId: string,
  data: Partial<GamificationData>
): Promise<void> {
  const payload: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };
  if (data.crystals !== undefined) payload.crystals = data.crystals;
  if (data.xp !== undefined) payload.xp = data.xp;
  if (data.currentStreak !== undefined) payload.current_streak = data.currentStreak;
  if (data.lastActivityDate !== undefined) payload.last_activity_date = data.lastActivityDate;
  if (data.studyHistory !== undefined) payload.study_history = data.studyHistory;
  if (data.stats !== undefined) payload.stats = data.stats;
  if (data.ownedItems !== undefined) payload.owned_items = data.ownedItems;
  if (data.equippedItems !== undefined) payload.equipped_items = data.equippedItems;
  if (data.equippedTheme !== undefined) payload.equipped_theme = data.equippedTheme;
  if (data.equippedCardSkin !== undefined) payload.equipped_card_skin = data.equippedCardSkin;

  const { error } = await supabase.from("profiles").update(payload).eq("id", userId);

  if (error) {
    // Column may not exist if migration not run - log but don't throw
    console.warn("[upsertGamification] Supabase error (migration may be needed):", error);
  }
}

/** Error code thrown when profiles insert/update fails because auth user no longer exists (e.g. manually deleted). */
export const AUTH_USER_MISSING_ERROR_CODE = "AUTH_USER_MISSING";

/** Create or update profile with completed registration data.
 * Uses upsert: INSERT if no row exists, UPDATE if row exists (e.g. from trigger).
 * On FK violation 23503 (auth user missing), throws error with code AUTH_USER_MISSING_ERROR_CODE so caller can sign out and show recovery message.
 */
export async function upsertProfile(
  userId: string,
  data: { username: string; birth_date: string; is_teacher: boolean }
): Promise<void> {
  const payload = {
    id: userId,
    username: data.username,
    birth_date: data.birth_date,
    is_teacher: data.is_teacher,
    updated_at: new Date().toISOString(),
  };

  try {
    if (typeof console.debug === "function") {
      console.debug("[complete_registration] ensure_profile_row_started", { userId: userId ? "present" : "missing" });
    }
    const { error } = await supabase
      .from("profiles")
      .upsert(payload, { onConflict: "id" });

    if (error) {
      if (error.code === "23503") {
        if (typeof console.warn === "function") {
          console.warn("[complete_registration] ensure_profile_row_recovery_failed", {
            code: error.code,
            reason: "auth_user_missing",
          });
        }
        const e = new Error(error.message) as Error & { code: string };
        e.code = AUTH_USER_MISSING_ERROR_CODE;
        throw e;
      }
      console.error("[upsertProfile] Supabase error:", {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        full: error,
      });
      throw error;
    }
    if (typeof console.debug === "function") {
      console.debug("[complete_registration] ensure_profile_row_created");
    }
  } catch (err) {
    if (err && typeof err === "object" && (err as { code?: string }).code === AUTH_USER_MISSING_ERROR_CODE) {
      throw err;
    }
    console.error("[upsertProfile] Database update failed:", err);
    throw err;
  }
}

// ─── Leaderboard ──────────────────────────────────────────────────────────────

export interface LeaderboardRow {
  rank: number;
  id: string;
  username: string | null;
  avatar_url: string | null;
  score: number;
}

/** Total number of registered users (profiles table). For admin analytics. RLS may require a policy allowing count. */
export async function getTotalUsersCount(): Promise<number> {
  const { count, error } = await supabase
    .from("profiles")
    .select("id", { count: "exact", head: true });
  if (error) throw error;
  return count ?? 0;
}

/** Top 100 students by crystals (requires RLS policy "Authenticated can read profiles for leaderboard"). */
export async function fetchStudentLeaderboard(): Promise<LeaderboardRow[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, username, avatar_url, crystals")
    .order("crystals", { ascending: false })
    .limit(100);

  if (error) throw error;
  const rows = (data ?? []) as { id: string; username: string | null; avatar_url: string | null; crystals: number }[];
  return rows.map((r, i) => ({
    rank: i + 1,
    id: r.id,
    username: r.username ?? null,
    avatar_url: r.avatar_url ?? null,
    score: Number(r.crystals) || 0,
  }));
}

/** Top 100 creators by cards created (RPC). */
export async function fetchCreatorLeaderboard(currentUserId?: string): Promise<LeaderboardRow[]> {
  const { data, error } = await supabase.rpc("get_creator_leaderboard");
  if (error) throw error;
  const rows = (data ?? []) as { rank: number; id: string; username: string | null; avatar_url: string | null; cards_count: number }[];
  let mapped = rows.map((r) => ({
    rank: Number(r.rank) || 0,
    id: r.id,
    username: r.username ?? null,
    avatar_url: r.avatar_url ?? null,
    score: Number(r.cards_count) || 0,
  }));

  if (!currentUserId) return mapped;

  const exactScore = await fetchExactCreatedCardsCount(currentUserId);
  if (!mapped.some((row) => row.id === currentUserId)) return mapped;

  mapped = mapped
    .map((row) => (row.id === currentUserId ? { ...row, score: exactScore } : row))
    .sort((a, b) => b.score - a.score || a.rank - b.rank || a.id.localeCompare(b.id))
    .map((row, index) => ({ ...row, rank: index + 1 }));

  return mapped;
}

export interface MyRankResult {
  rank: number;
  score: number;
}

/** Count only cards in decks created from scratch (source_deck_id IS NULL). Used for creator leaderboard. */
export async function fetchCreatorCardsCount(userId: string): Promise<number> {
  const { data: decksData, error: deckError } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId)
    .is("source_deck_id", null);
  if (deckError) throw deckError;
  const deckIds = (decksData ?? []).map((d) => d.id);
  if (deckIds.length === 0) return 0;
  let cardCount = 0;
  const batchSize = 200;
  for (let i = 0; i < deckIds.length; i += batchSize) {
    const batch = deckIds.slice(i, i + batchSize);
    const { count, error: cardError } = await supabase
      .from("cards")
      .select("id", { count: "exact", head: true })
      .in("deck_id", batch);
    if (cardError) throw cardError;
    cardCount += count ?? 0;
  }
  return cardCount;
}

export async function fetchExactCreatedCardsCount(userId: string): Promise<number> {
  return fetchCreatorCardsCount(userId);
}

/** Current user's student rank and crystals (RPC). */
export async function fetchMyStudentRank(userId: string): Promise<MyRankResult | null> {
  const { data, error } = await supabase.rpc("get_my_student_rank", { p_user_id: userId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  const r = row as { rank?: number; crystals?: number };
  return { rank: Number(r.rank) || 0, score: Number(r.crystals) || 0 };
}

/** Current user's creator rank and cards count (RPC). */
export async function fetchMyCreatorRank(userId: string): Promise<MyRankResult | null> {
  const leaderboard = await fetchCreatorLeaderboard(userId);
  const currentFromLeaderboard = leaderboard.find((row) => row.id === userId);
  if (currentFromLeaderboard) {
    return {
      rank: currentFromLeaderboard.rank,
      score: currentFromLeaderboard.score,
    };
  }

  const { data, error } = await supabase.rpc("get_my_creator_rank", { p_user_id: userId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  const r = row as { rank?: number; cards_count?: number };
  const exactScore = await fetchExactCreatedCardsCount(userId);
  return { rank: Number(r.rank) || 0, score: exactScore };
}

// ─── Card Favorites ───────────────────────────────────────────────────────────

export interface FavoriteCardWithDeck {
  card: Card;
  deckId: string;
  deckTitle: string;
  deckEmoji: string;
}

/** Add a card to user's favorites */
export async function addCardFavorite(userId: string, cardId: string): Promise<void> {
  const { error } = await supabase.from("card_favorites").upsert(
    { user_id: userId, card_id: cardId },
    { onConflict: "user_id,card_id" }
  );
  if (error) throw error;
}

/** Remove a card from user's favorites */
export async function removeCardFavorite(userId: string, cardId: string): Promise<void> {
  const { error } = await supabase
    .from("card_favorites")
    .delete()
    .eq("user_id", userId)
    .eq("card_id", cardId);
  if (error) throw error;
}

/** Check if cards are favorited (returns Set of card IDs) */
export async function fetchFavoriteCardIds(userId: string, cardIds: string[]): Promise<Set<string>> {
  if (cardIds.length === 0) return new Set();
  const { data, error } = await supabase
    .from("card_favorites")
    .select("card_id")
    .eq("user_id", userId)
    .in("card_id", cardIds);
  if (error) throw error;
  return new Set((data ?? []).map((r) => r.card_id));
}

/** Fetch all favorite cards for the user with deck info */
export async function fetchFavoriteCards(userId: string): Promise<FavoriteCardWithDeck[]> {
  const { data: favData, error: favError } = await supabase
    .from("card_favorites")
    .select("card_id")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (favError) throw favError;
  if (!favData || favData.length === 0) return [];

  const cardIds = favData.map((r) => r.card_id);

  const { data: cardsData, error: cardsError } = await supabase
    .from("cards")
    .select("*")
    .in("id", cardIds);

  if (cardsError) throw cardsError;
  if (!cardsData || cardsData.length === 0) return [];

  const cardRows = cardsData as CardsRow[];
  const deckIds = [...new Set(cardRows.map((c) => c.deck_id))];
  const { data: decksData, error: decksError } = await supabase
    .from("decks")
    .select("id, title, emoji, user_id")
    .in("id", deckIds)
    .eq("user_id", userId);

  if (decksError) throw decksError;
  const deckMap = new Map(
    (decksData ?? []).map((d) => [d.id, { id: d.id, title: d.title, emoji: d.emoji ?? "📚" }])
  );

  const result: FavoriteCardWithDeck[] = [];
  for (const row of cardRows) {
    const deck = deckMap.get(row.deck_id);
    if (!deck) continue;
    const card = mapCardRow(row);
    result.push({
      card,
      deckId: deck.id,
      deckTitle: deck.title,
      deckEmoji: deck.emoji,
    });
  }
  return result;
}

// ─── Adaptive Learning (user_card_progress, Smart Review) ─────────────────────

function mapUserCardProgressRowToProgress(row: UserCardProgressRow): UserCardProgress {
  return {
    userId: row.user_id,
    cardId: row.card_id,
    deckId: row.deck_id,
    timesSeen: row.times_seen,
    timesCorrect: row.times_correct,
    timesIncorrect: row.times_incorrect,
    currentStreak: row.current_streak,
    bestStreak: row.best_streak,
    avgResponseMs: row.avg_response_ms,
    lastResponseMs: row.last_response_ms,
    hintUsedCount: row.hint_used_count,
    explainUsedCount: row.explain_used_count,
    timesFlipped: row.times_flipped,
    learnSuccessCount: row.learn_success_count,
    testSuccessCount: row.test_success_count,
    gameSuccessCount: row.game_success_count,
    learnFailCount: row.learn_fail_count,
    testFailCount: row.test_fail_count,
    gameFailCount: row.game_fail_count,
    lastSeenAt: row.last_seen_at,
    lastCorrectAt: row.last_correct_at,
    lastIncorrectAt: row.last_incorrect_at,
    firstSeenAt: row.first_seen_at,
    difficultyScore: row.difficulty_score,
    difficultyLevel: row.difficulty_level,
    memoryStrength: row.memory_strength,
    forgettingRisk: row.forgetting_risk,
    stabilityScore: row.stability_score,
    nextReviewAt: row.next_review_at,
    overdueScore: row.overdue_score,
    reviewPriority: row.review_priority,
    recommendedMode: row.recommended_mode,
    lastReviewBucket: row.last_review_bucket,
    lastSmartReviewAt: row.last_smart_review_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function progressToRowPayload(p: Partial<UserCardProgress>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.timesSeen !== undefined) out.times_seen = p.timesSeen;
  if (p.timesCorrect !== undefined) out.times_correct = p.timesCorrect;
  if (p.timesIncorrect !== undefined) out.times_incorrect = p.timesIncorrect;
  if (p.currentStreak !== undefined) out.current_streak = p.currentStreak;
  if (p.bestStreak !== undefined) out.best_streak = p.bestStreak;
  if (p.avgResponseMs !== undefined) out.avg_response_ms = p.avgResponseMs;
  if (p.lastResponseMs !== undefined) out.last_response_ms = p.lastResponseMs;
  if (p.hintUsedCount !== undefined) out.hint_used_count = p.hintUsedCount;
  if (p.explainUsedCount !== undefined) out.explain_used_count = p.explainUsedCount;
  if (p.timesFlipped !== undefined) out.times_flipped = p.timesFlipped;
  if (p.learnSuccessCount !== undefined) out.learn_success_count = p.learnSuccessCount;
  if (p.testSuccessCount !== undefined) out.test_success_count = p.testSuccessCount;
  if (p.gameSuccessCount !== undefined) out.game_success_count = p.gameSuccessCount;
  if (p.learnFailCount !== undefined) out.learn_fail_count = p.learnFailCount;
  if (p.testFailCount !== undefined) out.test_fail_count = p.testFailCount;
  if (p.gameFailCount !== undefined) out.game_fail_count = p.gameFailCount;
  if (p.lastSeenAt !== undefined) out.last_seen_at = p.lastSeenAt;
  if (p.lastCorrectAt !== undefined) out.last_correct_at = p.lastCorrectAt;
  if (p.lastIncorrectAt !== undefined) out.last_incorrect_at = p.lastIncorrectAt;
  if (p.firstSeenAt !== undefined) out.first_seen_at = p.firstSeenAt;
  if (p.difficultyScore !== undefined) out.difficulty_score = p.difficultyScore;
  if (p.difficultyLevel !== undefined) out.difficulty_level = p.difficultyLevel;
  if (p.memoryStrength !== undefined) out.memory_strength = p.memoryStrength;
  if (p.forgettingRisk !== undefined) out.forgetting_risk = p.forgettingRisk;
  if (p.stabilityScore !== undefined) out.stability_score = p.stabilityScore;
  if (p.nextReviewAt !== undefined) out.next_review_at = p.nextReviewAt;
  if (p.overdueScore !== undefined) out.overdue_score = p.overdueScore;
  if (p.reviewPriority !== undefined) out.review_priority = p.reviewPriority;
  if (p.recommendedMode !== undefined) out.recommended_mode = p.recommendedMode;
  if (p.lastReviewBucket !== undefined) out.last_review_bucket = p.lastReviewBucket;
  if (p.lastSmartReviewAt !== undefined) out.last_smart_review_at = p.lastSmartReviewAt;
  if (p.updatedAt !== undefined) out.updated_at = p.updatedAt;
  return out;
}

function isMissingUserCardProgressError(error: { code?: string; message?: string } | null | undefined): boolean {
  const msg = String(error?.message ?? "");
  return (
    error?.code === "PGRST205" ||
    /relation .* does not exist/i.test(msg) ||
    /user_card_progress/i.test(msg)
  );
}

/** Upsert user_card_progress. Fails gracefully if table missing. */
export async function upsertUserCardProgress(
  userId: string,
  cardId: string,
  deckId: string,
  updates: Partial<UserCardProgress>
): Promise<void> {
  const id = `ucp_${userId}_${cardId}`;
  const payload = {
    id,
    user_id: userId,
    card_id: cardId,
    deck_id: deckId,
    ...progressToRowPayload(updates),
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from("user_card_progress").upsert(payload, {
    onConflict: "user_id,card_id",
  });
  if (error) {
    if (isMissingUserCardProgressError(error)) return;
    throw error;
  }
}

/** Get progress for one user-card. Returns null if not found or table missing. */
export async function getUserCardProgress(userId: string, cardId: string): Promise<UserCardProgress | null> {
  const { data, error } = await supabase
    .from("user_card_progress")
    .select("*")
    .eq("user_id", userId)
    .eq("card_id", cardId)
    .maybeSingle();

  if (error) {
    if (isMissingUserCardProgressError(error)) return null;
    throw error;
  }
  if (!data) return null;
  return mapUserCardProgressRowToProgress(data as UserCardProgressRow);
}

/** Record one card interaction and update aggregate progress. */
export async function recordCardInteraction(input: CardInteractionInput): Promise<void> {
  const { userId, cardId, deckId, mode, answeredCorrectly, responseTimeMs, hintUsed, explainUsed, flipped, occurredAt } = input;
  const now = occurredAt ?? new Date().toISOString();

  const existing = await getUserCardProgress(userId, cardId);
  const prev: UserCardProgress = existing ?? {
    userId,
    cardId,
    deckId,
    timesSeen: 0,
    timesCorrect: 0,
    timesIncorrect: 0,
    currentStreak: 0,
    bestStreak: 0,
    avgResponseMs: null,
    lastResponseMs: null,
    hintUsedCount: 0,
    explainUsedCount: 0,
    timesFlipped: 0,
    learnSuccessCount: 0,
    testSuccessCount: 0,
    gameSuccessCount: 0,
    learnFailCount: 0,
    testFailCount: 0,
    gameFailCount: 0,
    lastSeenAt: null,
    lastCorrectAt: null,
    lastIncorrectAt: null,
    firstSeenAt: null,
    difficultyScore: null,
    difficultyLevel: null,
    memoryStrength: null,
    forgettingRisk: null,
    stabilityScore: null,
    nextReviewAt: null,
    overdueScore: null,
    reviewPriority: null,
    recommendedMode: null,
    lastReviewBucket: null,
    lastSmartReviewAt: null,
    createdAt: now,
    updatedAt: now,
  };

  const timesSeen = prev.timesSeen + 1;
  const timesCorrect = prev.timesCorrect + (answeredCorrectly ? 1 : 0);
  const timesIncorrect = prev.timesIncorrect + (answeredCorrectly ? 0 : 1);
  const currentStreak = answeredCorrectly ? prev.currentStreak + 1 : 0;
  const bestStreak = Math.max(prev.bestStreak, currentStreak);
  const firstSeenAt = prev.firstSeenAt ?? now;
  const lastSeenAt = now;
  const lastCorrectAt = answeredCorrectly ? now : prev.lastCorrectAt;
  const lastIncorrectAt = answeredCorrectly ? prev.lastIncorrectAt : now;

  let avgResponseMs = prev.avgResponseMs;
  if (responseTimeMs != null && responseTimeMs >= 0) {
    avgResponseMs = prev.timesSeen === 0 ? responseTimeMs : Math.round(((prev.avgResponseMs ?? 0) * prev.timesSeen + responseTimeMs) / timesSeen);
  }

  const hintUsedCount = prev.hintUsedCount + (hintUsed ? 1 : 0);
  const explainUsedCount = prev.explainUsedCount + (explainUsed ? 1 : 0);
  const timesFlipped = prev.timesFlipped + (flipped ? 1 : 0);

  let learnSuccessCount = prev.learnSuccessCount;
  let learnFailCount = prev.learnFailCount;
  let testSuccessCount = prev.testSuccessCount;
  let testFailCount = prev.testFailCount;
  let gameSuccessCount = prev.gameSuccessCount;
  let gameFailCount = prev.gameFailCount;
  if (mode === "learn") {
    if (answeredCorrectly) learnSuccessCount++; else learnFailCount++;
  } else if (mode === "test") {
    if (answeredCorrectly) testSuccessCount++; else testFailCount++;
  } else if (mode === "games") {
    if (answeredCorrectly) gameSuccessCount++; else gameFailCount++;
  }

  const updated: UserCardProgress = {
    ...prev,
    timesSeen,
    timesCorrect,
    timesIncorrect,
    currentStreak,
    bestStreak,
    avgResponseMs: avgResponseMs ?? null,
    lastResponseMs: responseTimeMs ?? null,
    hintUsedCount,
    explainUsedCount,
    timesFlipped,
    learnSuccessCount,
    testSuccessCount,
    gameSuccessCount,
    learnFailCount,
    testFailCount,
    gameFailCount,
    firstSeenAt,
    lastSeenAt,
    lastCorrectAt,
    lastIncorrectAt,
    updatedAt: now,
  };

  const recomputed = recomputeAdaptiveMetrics(updated, new Date(now));
  const fullUpdate: Partial<UserCardProgress> = { ...updated, ...recomputed };

  await upsertUserCardProgress(userId, cardId, deckId, fullUpdate);

  // Record study activity for Statistics (heatmap, activity count)
  try {
    const today = new Date().toISOString().slice(0, 10);
    await upsertUserActivity(userId, today, 1);
  } catch {
    // ignore — user_activity may not exist
  }

  // Ensure minimal study time is tracked: 30s per card so short sessions (1 card) still show in stats
  try {
    useTimeTrackingStore.getState().addStudySeconds(30);
  } catch {
    // ignore
  }

  // Keep legacy progress table in sync for backward compatibility
  try {
    await upsertProgress(userId, cardId, answeredCorrectly);
  } catch {
    // ignore
  }

  try {
    await supabase.from("user_card_events").insert({
      user_id: userId,
      card_id: cardId,
      deck_id: deckId,
      mode,
      answered_correctly: answeredCorrectly,
      response_time_ms: responseTimeMs ?? null,
      hint_used: hintUsed ?? false,
      explain_used: explainUsed ?? false,
      flipped: flipped ?? false,
      occurred_at: now,
    });
  } catch (e) {
    console.warn("[recordCardInteraction] user_card_events insert failed:", e);
  }
}

async function fetchCardsByIdsForAdaptive(cardIds: string[]): Promise<Card[]> {
  if (cardIds.length === 0) return [];
  const { data, error } = await supabase
    .from("cards")
    .select("*")
    .in("id", cardIds);
  if (error) throw error;
  const rows = (data ?? []) as CardsRow[];
  return rows.map((r) => mapCardRow(r));
}

/** Get candidates for Smart Review. */
export async function getSmartReviewCandidates(
  userId: string,
  options: SmartReviewOptions
): Promise<SmartReviewCandidate[]> {
  const { data: deckRows, error: deckErr } = await supabase
    .from("decks")
    .select("id")
    .eq("user_id", userId);
  if (deckErr || !deckRows?.length) return [];

  const deckIds = options.deckIds ?? deckRows.map((d) => d.id);
  if (deckIds.length === 0) return [];

  const { data: cardRows, error: cardErr } = await supabase
    .from("cards")
    .select("id, deck_id")
    .in("deck_id", deckIds);
  if (cardErr || !cardRows?.length) return [];

  const cardIds = (cardRows as { id: string; deck_id: string }[]).map((c) => c.id);
  let progressRows: UserCardProgressRow[] = [];
  try {
    const { data: progData, error: progErr } = await supabase
      .from("user_card_progress")
      .select("*")
      .eq("user_id", userId)
      .in("card_id", cardIds);
    if (!progErr && progData?.length) progressRows = progData as UserCardProgressRow[];
  } catch {
    // table may not exist
  }

  const progressByCard = new Map<string, UserCardProgress>();
  for (const row of progressRows) {
    progressByCard.set(row.card_id, mapUserCardProgressRowToProgress(row));
  }

  const now = new Date();
  const preset = options.preset ?? "today";

  const candidates: SmartReviewCandidate[] = [];
  for (const c of cardRows as { id: string; deck_id: string }[]) {
    const progress = progressByCard.get(c.id) ?? null;
    const nextReview = progress?.nextReviewAt ?? null;
    const isDue = !nextReview || new Date(nextReview) <= now;
    const isWeak = progress && (progress.difficultyLevel === "hard" || progress.difficultyLevel === "critical");
    const isNew = !progress || progress.timesSeen === 0;

    if (preset === "today" && (isDue || isWeak || (options.includeNew && isNew))) {
      candidates.push({ cardId: c.id, deckId: c.deck_id, progress });
    } else if (preset === "overdue" && isDue && nextReview) {
      candidates.push({ cardId: c.id, deckId: c.deck_id, progress });
    } else if (preset === "weak" && isWeak) {
      candidates.push({ cardId: c.id, deckId: c.deck_id, progress });
    } else if (preset === "repeat" && ((isDue && nextReview) || isWeak)) {
      candidates.push({ cardId: c.id, deckId: c.deck_id, progress });
    } else if (preset === "fast" || preset === "recovery") {
      if (isDue || isWeak || (options.includeNew && isNew)) {
        candidates.push({ cardId: c.id, deckId: c.deck_id, progress });
      }
    } else if (!options.preset) {
      candidates.push({ cardId: c.id, deckId: c.deck_id, progress });
    }
  }
  return candidates;
}

/** Build and return a Smart Review session with full card objects. */
export async function getSmartReviewSession(
  userId: string,
  options: SmartReviewOptions = {}
): Promise<SmartReviewSessionResult & { cards: Card[] }> {
  const candidates = await getSmartReviewCandidates(userId, options);
  const now = new Date();
  const { cardIds, deckIdByCardId, summary } = buildSmartReviewSession(candidates, options, now);

  const cards = await fetchCardsByIdsForAdaptive(cardIds);
  const cardOrder = new Map(cardIds.map((id, i) => [id, i]));
  cards.sort((a, b) => (cardOrder.get(a.id!) ?? 0) - (cardOrder.get(b.id!) ?? 0));

  return {
    cardIds,
    deckIdByCardId,
    totalCount: cards.length,
    summary,
    cards,
  };
}

/** Recompute adaptive metrics for one user-card and persist. */
export async function recomputeAdaptiveMetricsForCard(userId: string, cardId: string): Promise<void> {
  const progress = await getUserCardProgress(userId, cardId);
  if (!progress) return;
  const updates = recomputeAdaptiveMetrics(progress, new Date());
  await upsertUserCardProgress(userId, cardId, progress.deckId, updates);
}

/** Batch recompute adaptive metrics for a user. Processes up to limit rows. Call in background (e.g. when opening Smart Review). */
export async function recomputeAdaptiveMetricsForUser(
  userId: string,
  options?: { limit?: number }
): Promise<{ processed: number }> {
  const limit = options?.limit ?? 200;
  try {
    const { data: rows, error } = await supabase
      .from("user_card_progress")
      .select("*")
      .eq("user_id", userId)
      .limit(limit);

    if (error || !rows?.length) return { processed: 0 };
    const now = new Date();
    let processed = 0;
    for (const row of rows as UserCardProgressRow[]) {
      const progress = mapUserCardProgressRowToProgress(row);
      const updates = recomputeAdaptiveMetrics(progress, now);
      await upsertUserCardProgress(userId, progress.cardId, progress.deckId, updates);
      processed++;
    }
    return { processed };
  } catch {
    return { processed: 0 };
  }
}

/** Get due card count for user (from user_card_progress). */
export async function getDueCardsCountForUser(userId: string): Promise<number> {
  try {
    const now = new Date().toISOString();
    const { count, error } = await supabase
      .from("user_card_progress")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .or(`next_review_at.is.null,next_review_at.lte."${now}"`);
    if (error || count == null) return 0;
    return count;
  } catch {
    return 0;
  }
}

/** Get weak card count (hard + critical). */
export async function getWeakCardsCountForUser(userId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from("user_card_progress")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .in("difficulty_level", ["hard", "critical"]);
    if (error || count == null) return 0;
    return count;
  } catch {
    return 0;
  }
}

// ─── AI Explanation Caching ───────────────────────────────────────────────────

/** Saves AI explanation for a card via RPC (works for owners + shared deck viewers) */
export async function saveCardAiExplanation(
  cardId: string,
  explanation: string
): Promise<void> {
  const { error } = await supabase.rpc("save_card_ai_explanation", {
    p_card_id: cardId,
    p_explanation: explanation,
  });
  if (error) throw error;
}
